import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  Package,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Truck,
  Search,
  Eye,
  ExternalLink,
  Download,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FileDown,
  PackageCheck,
  RefreshCw,
  ChevronLeft,
  Home,
  X,
  Edit,
  Save,
  Wallet,
  Info,
  GitMerge
} from 'lucide-react';
import { TailwindDialog, DialogContent, DialogActions, Button, Card, CardBody, Badge, StatusBadge } from '../../../components/ui';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/datepicker-custom.css';

// Composant pour Material Symbols
const MaterialIcon = ({ icon, className = "", style = {} }: { icon: string, className?: string, style?: React.CSSProperties }) => (
  <span className={`material-symbols-outlined ${className}`} style={style}>
    {icon}
  </span>
);

import { useAuth } from '@/app/providers';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { OrderRepository } from '../../../services/firebase/order-repository';
import { toast } from 'react-hot-toast';
import { Product, FirebaseOrder } from '../../../models/types';
import { exportOrdersToCsv } from '@/shared/lib/exportUtils';
import { useOrgaConfiguration } from '../../../shared/hooks/useOrgaConfiguration';
import { getOrganizationId } from '@/shared/lib/brandUtils';
import type { DocumentSnapshot } from 'firebase/firestore';
import { useOrgaCompletion } from '@/shared/hooks/useOrgaCompletion';
import { StripeExpressDashboardService } from '../../../services/stripe/stripe-express-dashboard-service';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { useTranslation } from 'react-i18next';
import GooglePlacesSearch from '@/components/shared/GooglePlacesSearch';
import SeoTags from '@/components/shared/SeoTags';
import { OrganizationBalanceClientRepository } from '../../../services/firebase/organization-balance-repository';
import { KPICard, KPICardGrid } from './analytics/KPICard';
import { BatchParcelModal } from './BatchParcelModal';
import { useShippingMethods } from '../../order-journey/hooks/useShippingMethods';
import { PickupService } from '@/services/shipping/pickup-service';
import { PickupPoint as PickupPointType } from '@/models';
import {
  resolveShippingMethodConfigId,
  formatShippingMethodIdForDisplay,
} from '@/shared/utils/shippingMethodConfigId';

/** ID méthode affiché / édité dans l’admin (aligné sur les Cloud Functions). */
function resolveDashboardShippingMethodId(
  deliveryInfo: FirebaseOrder['deliveryInfo'] | undefined,
): string | null {
  return formatShippingMethodIdForDisplay(resolveShippingMethodConfigId(deliveryInfo ?? null));
}

// Mini Stepper élégant avec Material UI - Utilise les variables CSS du thème
const MiniStepIcon = styled('div')<{ ownerState: { active: boolean; completed: boolean; iconType: 'clock' | 'chef' | 'check' } }>(
  ({ ownerState }) => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    position: 'relative',
    boxShadow: ownerState.active ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)',
    backgroundColor: (() => {
      if (ownerState.active) {
        if (ownerState.iconType === 'clock') return 'var(--warning)';
        if (ownerState.iconType === 'chef') return 'var(--primary)';
        return 'var(--success)';
      }
      if (ownerState.completed) return 'var(--success)';
      return 'var(--border-medium)';
    })(),
    color: (ownerState.active || ownerState.completed) ? 'var(--on-primary)' : 'var(--foreground-secondary)',
    '&:hover': {
      transform: 'scale(1.15)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      backgroundColor: (() => {
        if (ownerState.iconType === 'clock') return 'var(--warning)';
        if (ownerState.iconType === 'chef') return 'var(--primary)';
        return 'var(--success)';
      })(),
      color: 'var(--on-primary)',
    },
    '&:active': {
      transform: 'scale(1.05)',
    }
  })
);

const CompactStepper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 0',

  '& .step-connector': {
    width: '16px',
    height: '2px',
    borderRadius: '1px',
    transition: 'background-color 0.3s ease',
    backgroundColor: 'var(--border-medium)',

    '&.active': {
      backgroundColor: 'var(--success)',
    }
  }
});

const NewAdminDashboard = () => {
  const { t } = useTranslation();
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { idorga } = useParams<{ idorga: string }>();
  const { configuration } = useOrgaConfiguration();
  const { canViewAnalytics, canManageOrders, isAdminForOrganization: checkIsAdminForOrganization } = usePermissionsHook();

  // Vérifier si l'utilisateur peut voir les statistiques
  const canViewStats = idorga ? canViewAnalytics(idorga) : false;

  // Vérifier si l'utilisateur est admin pour cette organisation
  const isAdminForOrg = idorga ? (isAdmin || checkIsAdminForOrganization(idorga)) : false;

  // Vérifier si l'utilisateur peut gérer les commandes
  const canManageOrdersForOrg = idorga ? canManageOrders(idorga) : false;
  const [isOpeningDashboard, setIsOpeningDashboard] = useState(false);
  const [orders, setOrders] = useState<FirebaseOrder[]>([]); // Commandes chargées (pagination Firestore)
  const [filteredOrders, setFilteredOrders] = useState<FirebaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  // Pagination server-side removed in favor of real-time subscription
  const ordersPageLimit = 50; // Kept if referenced elsewhere, though effective limit is now infinite

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>(['all']);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const statusOptions = [
    { value: 'all', label: t('admin.dashboard.table.allStatuses') },
    { value: 'en_cours', label: t('admin.dashboard.filters.inProgress') },
    { value: 'paid', label: t('admin.dashboard.filters.paidStatus') },
    { value: 'no_tracking', label: t('admin.dashboard.filters.noTracking') },
    { value: 'to_generate', label: t('admin.dashboard.filters.toGenerate', 'À générer') },
    { value: 'todo', label: t('admin.dashboard.filters.todo') },
    { value: 'in_progress', label: t('admin.dashboard.filters.preparation') },
    { value: 'ready', label: t('admin.dashboard.filters.ready') },
    { value: 'cancelled', label: t('admin.dashboard.filters.cancelled') },
    { value: 'refunded', label: t('admin.dashboard.filters.refunded', 'Remboursé') },
  ];

  const toggleStatus = (status: string) => {
    if (status === 'all') {
      setStatusFilter(['all']);
    } else {
      let next = statusFilter.filter(s => s !== 'all');
      if (next.includes(status)) {
        next = next.filter(s => s !== status);
      } else {
        next.push(status);
      }

      if (next.length === 0) {
        setStatusFilter(['all']);
      } else {
        setStatusFilter(next);
      }
    }
  };
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<FirebaseOrder | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [expandedHistories, setExpandedHistories] = useState<Set<string>>(new Set());
  const [expandedPaymentDetails, setExpandedPaymentDetails] = useState<Set<string>>(new Set());
  useEffect(() => {
    setExpandedHistories(new Set());
    setExpandedPaymentDetails(new Set());
  }, [showOrderModal, selectedOrder]);

  // Resynchroniser la commande sélectionnée quand la liste des commandes change (temps réel : dettes, statuts, etc.)
  useEffect(() => {
    if (!selectedOrder?.id || !orders.length) return;
    const updated = orders.find((o) => o.id === selectedOrder.id);
    if (updated && updated !== selectedOrder) {
      setSelectedOrder(updated);
    }
  }, [orders]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Refund modal state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditOrder, setCreditOrder] = useState<FirebaseOrder | null>(null);
  const [creditProductIndex, setCreditProductIndex] = useState<number>(0); // Index du produit à rembourser
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditDescription, setCreditDescription] = useState<string>('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false); // true si on rembourse produit + frais de livraison

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<{
    type: 'paid' | 'failed';
    orderId: string;
    productIndex: number;
    productReference: string;
  } | null>(null);

  // Full order refund confirmation modal
  const [showFullRefundModal, setShowFullRefundModal] = useState(false);
  const [fullRefundOrder, setFullRefundOrder] = useState<FirebaseOrder | null>(null);
  const [fullRefundIncludeShipping, setFullRefundIncludeShipping] = useState(false);

  // Address editing state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({
    address: '',
    addressLine2: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: 'FR'
  });

  // State pour le champ de recherche d'adresse (indépendant du formulaire)
  const [addressSearchText, setAddressSearchText] = useState('');
  const [isRegeneratingParcel, setIsRegeneratingParcel] = useState(false);

  // State pour le solde de l'organisation (dette globale)
  const [organizationBalance, setOrganizationBalance] = useState<number | null>(null);

  // State pour le modal de détail de dette
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [selectedDebtOrder, setSelectedDebtOrder] = useState<FirebaseOrder | null>(null);

  // State pour la multi-sélection et la génération de colis en lot
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [showBatchParcelModal, setShowBatchParcelModal] = useState(false);

  // Vérifier si l'organisation utilise le mode API Key ou PayPal (pour la génération de colis manuelle)
  const paymentMode = configuration?.paymentMode;
  const isManualParcelMode = paymentMode === 'stripe_api_key' || paymentMode === 'paypal';

  // State pour le modal de création de retour Sendcloud

  // State pour le mode de sélection batch (génération de colis en lot)
  const [isBatchSelectionMode, setIsBatchSelectionMode] = useState(false);

  // Clôture manuelle de la période de fusion (frais de livraison)
  const [isClosingMergePeriod, setIsClosingMergePeriod] = useState(false);

  // Delivery methods and pickup points states
  const { getShippingMethodsForCountry, getShippingMethodById } = useShippingMethods();
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [parcelWeightKg, setParcelWeightKg] = useState<number>(0.5);
  const [methodSearch, setMethodSearch] = useState<string>('');
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPointType | null>(null);
  const [pickupPoints, setPickupPoints] = useState<PickupPointType[]>([]);
  const [isSearchingPickup, setIsSearchingPickup] = useState(false);
  const [pickupSearchPostalCode, setPickupSearchPostalCode] = useState('');



  // Sync address form with selected order (utiliser les valeurs brutes telles quelles)
  useEffect(() => {
    if (selectedOrder?.deliveryInfo) {
      setAddressForm({
        address: selectedOrder.deliveryInfo.address || '',
        addressLine2: selectedOrder.deliveryInfo.addressLine2 || '',
        houseNumber: selectedOrder.deliveryInfo.houseNumber || '',
        postalCode: selectedOrder.deliveryInfo.postalCode || '',
        city: selectedOrder.deliveryInfo.city || '',
        country: selectedOrder.deliveryInfo.country || ''
      });

      // Sync shipping method and pickup point (nouveau modèle : deliveryMethod = id config orga)
      setSelectedMethodId(resolveDashboardShippingMethodId(selectedOrder.deliveryInfo));
      setParcelWeightKg(
        Number(
          selectedOrder.deliveryInfo.minWeight ??
          selectedOrder.deliveryInfo.weight ??
          0.5
        ) || 0.5
      );
      setMethodSearch('');
      setSelectedPickupPoint(selectedOrder.deliveryInfo.pickupPoint || null);
      if (selectedOrder.deliveryInfo.pickupPoint?.postalCode) {
        setPickupSearchPostalCode(selectedOrder.deliveryInfo.pickupPoint.postalCode);
      } else {
        setPickupSearchPostalCode(selectedOrder.deliveryInfo.postalCode || '');
      }
    } else {
      setSelectedMethodId(null);
      setParcelWeightKg(0.5);
      setMethodSearch('');
      setSelectedPickupPoint(null);
      setPickupPoints([]);
    }
  }, [selectedOrder]);

  // Reset method and pickup point when country changes if method not available in new country
  useEffect(() => {
    if (addressForm.country) {
      const methods = getShippingMethodsForCountry(addressForm.country);
      if (selectedMethodId && !methods.some(m => m.id === selectedMethodId)) {
        console.log('🌍 Country changed, resetting shipping method');
        setSelectedMethodId(methods.length > 0 ? methods[0].id : null);
        setSelectedPickupPoint(null);
      }
    }
  }, [addressForm.country, getShippingMethodsForCountry, selectedMethodId]);

  // Écouter le solde de l'organisation en temps réel
  useEffect(() => {
    if (!idorga) return;

    const unsubscribe = OrganizationBalanceClientRepository.listenToCurrentBalance(
      idorga,
      (balance) => {
        setOrganizationBalance(balance);
      }
    );

    return () => unsubscribe();
  }, [idorga]);

  // Statut global du portail (même logique que dans ConfigurationDashboard)
  const { isStripeValid, isPortalReady: portalReadyFromHook } = useOrgaCompletion();
  const isStripeReady = isStripeValid;
  const hasShippingConfigured = !!(configuration?.shippingConfig?.methods?.some(m => m.isActive));
  // Utiliser useOrgaCompletion pour une logique cohérente
  const isPortalReady = portalReadyFromHook;

  /** Résumé de la config merge (frais de livraison une fois pendant la période) pour affichage dashboard */
  const getMergeConfigSummary = useCallback((): string => {
    const mergeConfig = configuration?.shippingConfig?.orderMergeConfig;
    const ranges = mergeConfig?.mergeOpenDaysRanges?.filter(
      (r: { start?: number | null; end?: number | null }) => r.start != null && r.end != null
    ) ?? [];
    if (ranges.length === 0) {
      return t('admin.configuration.shipping.orderMergeConfig.summaryDefault');
    }
    const rangeDescriptions = ranges.map((r: { start: number; end: number }) => {
      const startDay = t(`admin.configuration.shipping.orderMergeConfig.weekday${r.start}`);
      const endDay = t(`admin.configuration.shipping.orderMergeConfig.weekday${r.end}`);
      return `${startDay} → ${endDay}`;
    });
    if (rangeDescriptions.length === 1) {
      return t('admin.configuration.shipping.orderMergeConfig.summaryOneRange', { range: rangeDescriptions[0] });
    }
    return t('admin.configuration.shipping.orderMergeConfig.summaryMultipleRanges', { ranges: rangeDescriptions.join(', ') });
  }, [configuration?.shippingConfig?.orderMergeConfig, t]);

  // Date filters avec react-datepicker
  // Par défaut : aujourd'hui du matin au soir
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Début de journée (00:00:00)
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999); // Fin de journée (23:59:59)
    return date;
  });

  // Open address edit modal
  const openAddressModal = (order: FirebaseOrder) => {
    if (!order.deliveryInfo) return;

    setAddressForm({
      address: order.deliveryInfo.address || '',
      addressLine2: order.deliveryInfo.addressLine2 || '',
      houseNumber: order.deliveryInfo.houseNumber || '',
      postalCode: order.deliveryInfo.postalCode || '',
      city: order.deliveryInfo.city || '',
      country: order.deliveryInfo.country || ''
    });

    setSelectedMethodId(resolveDashboardShippingMethodId(order.deliveryInfo));
    setSelectedPickupPoint(order.deliveryInfo.pickupPoint || null);
    if (order.deliveryInfo.pickupPoint?.postalCode) {
      setPickupSearchPostalCode(order.deliveryInfo.pickupPoint.postalCode);
    } else {
      setPickupSearchPostalCode(order.deliveryInfo.postalCode || '');
    }
    setPickupPoints([]);

    setSelectedOrder(order);
    setShowAddressModal(true);
  };

  // Helper for address extraction
  const extractAddressAndHouseNumber = (address: string, houseNumber: string) => {
    if (!address) return { address: '', houseNumber: houseNumber || '' };
    if (!houseNumber) return { address: address.trim(), houseNumber: '' };

    let cleaned = address.replace(houseNumber, '').trim();
    cleaned = cleaned.replace(/\s{2,}/g, ' '); // remove double spaces

    return {
      address: cleaned,
      houseNumber
    };
  };

  // Save address
  const handleSaveAddress = async () => {
    if (!selectedOrder) return;

    try {
      const rawHn = (addressForm.houseNumber || '').trim();
      let rawAddr = (addressForm.address || '').trim();
      const rawAddr2 = (addressForm.addressLine2 || '').trim();

      // Construire fullAddress en incluant addressLine2 si présent
      const fullAddress = [rawHn, rawAddr, rawAddr2, (addressForm.postalCode || '').trim(), (addressForm.city || '').trim(), addressForm.country]
        .filter(Boolean)
        .join(', ');

      await OrderRepository.updateDeliveryAddress(selectedOrder.id!, {
        address: rawAddr,
        addressLine2: rawAddr2 || undefined,
        fullAddress,
        houseNumber: rawHn,
        postalCode: (addressForm.postalCode || '').trim(),
        city: (addressForm.city || '').trim(),
        country: addressForm.country,
      });
      toast.success('Adresse mise à jour avec succès');
      setShowAddressModal(false);

      // Update local state
      if (selectedOrder.deliveryInfo) {
        setSelectedOrder({
          ...selectedOrder,
          deliveryInfo: {
            ...selectedOrder.deliveryInfo,
            address: rawAddr,
            addressLine2: rawAddr2 || undefined,
            houseNumber: rawHn,
            postalCode: (addressForm.postalCode || '').trim(),
            city: (addressForm.city || '').trim(),
            country: addressForm.country,
            fullAddress,
          }
        });
      }

      // Refresh orders
      refreshOrders();
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Erreur lors de la mise à jour de l\'adresse');
    }
  };

  // Regenerate parcel
  const handleRegenerateParcel = async (orderId: string) => {
    if (!orderId) return;

    setIsRegeneratingParcel(true);
    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/regenerateSendcloudParcel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to regenerate parcel');
      }

      toast.success('Colis Sendcloud régénéré avec succès');

      // Refresh orders
      await refreshOrders();

      // Update selected order if open
      if (selectedOrder && selectedOrder.id === orderId) {
        // Close modal to refresh or just re-fetch selected order
        setShowOrderModal(false);
      }
    } catch (error: any) {
      console.error('Error regenerating parcel:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setIsRegeneratingParcel(false);
    }
  };

  const handleAddressSelect = (address: string, city: string, postalCode: string, country: string, houseNumber: string, street: string) => {
    console.log('[NewAdminDashboard] Adresse sélectionnée:', { address, city, postalCode, country, houseNumber, street });

    // Champ "Rue/Voie" = route uniquement (sans numéro). Champ "N°" = numéro de rue.
    // address = fullAddress (numéro + rue) en entrée ; street = route seule.
    const rueVoie = (street || address || '').trim();
    const numero = (houseNumber || '').trim();

    setAddressForm(prev => ({
      ...prev,
      address: rueVoie,
      houseNumber: numero,
      city: city,
      postalCode: postalCode,
      country: country || 'FR' // Fallback FR si Google ne renvoie pas de pays (peu probable)
    }));
  };

  const handleMethodChange = (methodId: string) => {
    if (!selectedOrder?.deliveryInfo) return;

    const currentProvider = String(selectedOrder.deliveryInfo.provider || '').toLowerCase();
    const currentMethod = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
    const currentCarrier = (currentMethod as any)?.carrier ?? (selectedOrder.deliveryInfo as any)?.carrier;
    const nextMethod = getShippingMethodById(methodId);
    const nextProvider = String(nextMethod?.provider || '').toLowerCase();
    const nextCarrier = (nextMethod as any)?.carrier;

    // Verrouiller provider + carrier (Sendcloud plateforme / Sendcloud API key).
    if ((currentProvider === 'sendcloud' || currentProvider === 'sendcloud_api_key') && nextProvider && nextProvider !== currentProvider) {
      toast.error('Le provider ne peut pas être modifié');
      return;
    }
    if ((currentProvider === 'sendcloud' || currentProvider === 'sendcloud_api_key') && currentCarrier && nextCarrier && nextCarrier !== currentCarrier) {
      toast.error('Le transporteur (carrier) ne peut pas être modifié');
      return;
    }

    setSelectedMethodId(methodId);
    const method = nextMethod;
    if (method?.isHomeDelivery) {
      setSelectedPickupPoint(null);
    }
  };

  const handleSearchPickupPoints = async () => {
    if (!pickupSearchPostalCode || !addressForm.country || !idorga) return;

    const method = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
    if (method?.isHomeDelivery) {
      toast.error('Pas de point relais pour une livraison à domicile');
      return;
    }
    if (!selectedMethodId) {
      toast.error('Sélectionnez une méthode de livraison');
      return;
    }

    setIsSearchingPickup(true);
    try {
      const points = await PickupService.fetchPickupPoints(
        pickupSearchPostalCode,
        idorga,
        selectedMethodId,
        addressForm.country,
      );
      setPickupPoints(points);
      if (points.length === 0) {
        toast.error('Aucun point relais trouvé pour ce code postal');
      }
    } catch (error) {
      console.error('Error searching pickup points:', error);
      toast.error('Erreur lors de la recherche des points relais');
    } finally {
      setIsSearchingPickup(false);
    }
  };

  // Update address and regenerate parcel
  const handleUpdateAndRegenerate = async () => {
    if (!selectedOrder) return;

    setIsRegeneratingParcel(true);
    try {
      const isLockedAfterParcel =
        Boolean((selectedOrder as any)?.parcelGenerated?.parcelId) ||
        Boolean((selectedOrder as any)?.parcelGenerated?.trackingNumber) ||
        Boolean((selectedOrder as any)?.parcelGenerated?.labelPrinterUrl);
      if (isLockedAfterParcel) {
        toast.error('Colis déjà généré : aucune modification n’est possible');
        return;
      }

      const rawHn = (addressForm.houseNumber || '').trim();
      let rawAddr = (addressForm.address || '').trim();
      const rawAddr2 = (addressForm.addressLine2 || '').trim();

      // Reconstruire l'adresse complète pour Firebase
      const finalAddress = [rawHn, rawAddr, rawAddr2, (addressForm.postalCode || '').trim(), (addressForm.city || '').trim(), addressForm.country]
        .filter(Boolean)
        .join(', ');

      // 1. Mettre à jour l'adresse et les infos de livraison dans la base de données
      const method = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
      const isMethodCompatibleWithWeight = (() => {
        if (!method) return true;
        const w = Number(parcelWeightKg) || 0;
        const min = (method as any).minWeight;
        const max = (method as any).maxWeight;
        if (typeof min === 'number' && Number.isFinite(min) && w < min) return false;
        if (typeof max === 'number' && Number.isFinite(max) && w > max) return false;
        return true;
      })();
      if (!isMethodCompatibleWithWeight) {
        toast.error('Le poids dépasse la plage de la méthode actuelle. Choisissez une méthode compatible.');
        return;
      }

      const numericSendcloudMethodId =
        method?.sendcloudMethodId ??
        (selectedMethodId && /^\d+$/.test(selectedMethodId) ? Number(selectedMethodId) : undefined) ??
        selectedOrder.deliveryInfo.methodId;

      await OrderRepository.updateDeliveryInfo({
        orderId: selectedOrder.id!,
        deliveryMethod: selectedMethodId,
        address: rawAddr,
        addressLine2: rawAddr2 || undefined,
        fullAddress: finalAddress,
        houseNumber: rawHn,
        postalCode: (addressForm.postalCode || '').trim(),
        city: (addressForm.city || '').trim(),
        country: addressForm.country,
        pickupPoint: selectedPickupPoint || undefined,
        weight: parcelWeightKg,
        minWeight: parcelWeightKg,
        methodId: numericSendcloudMethodId,
        methodName: method?.name || selectedOrder.deliveryInfo.methodName,
        methodDescription: method?.description || selectedOrder.deliveryInfo.methodDescription,
        isHomeDelivery: method ? method.isHomeDelivery : selectedOrder.deliveryInfo.isHomeDelivery,
        servicePointInput: selectedOrder.deliveryInfo.servicePointInput,
        shippingCost: method ? method.getPriceForCountry(addressForm.country) : selectedOrder.deliveryInfo.shippingCost,
        estimatedHours: method ? method.estimatedHours : selectedOrder.deliveryInfo.estimatedHours,
        provider: method?.provider || selectedOrder.deliveryInfo.provider || 'sendcloud',
      });

      // Update local state
      if (selectedOrder.deliveryInfo) {
        setSelectedOrder({
          ...selectedOrder,
          deliveryInfo: {
            ...selectedOrder.deliveryInfo,
            address: rawAddr,
            addressLine2: rawAddr2 || undefined,
            houseNumber: rawHn,
            postalCode: (addressForm.postalCode || '').trim(),
            city: (addressForm.city || '').trim(),
            country: addressForm.country,
            fullAddress: finalAddress,
            deliveryMethod: selectedMethodId ?? selectedOrder.deliveryInfo.deliveryMethod,
            methodId: numericSendcloudMethodId ?? selectedOrder.deliveryInfo.methodId,
            methodName: method?.name || selectedOrder.deliveryInfo.methodName,
            provider: method?.provider || selectedOrder.deliveryInfo.provider,
            isHomeDelivery: method ? method.isHomeDelivery : selectedOrder.deliveryInfo.isHomeDelivery,
            pickupPoint: selectedPickupPoint || undefined,
            shippingCost: method ? method.getPriceForCountry(addressForm.country) : selectedOrder.deliveryInfo.shippingCost,
            minWeight: parcelWeightKg,
            weight: parcelWeightKg,
            parcelCreationError: undefined // Reset error on success update
          }
        });
      }

      // 2. Rafraîchir
      await refreshOrders();

      // 3. Fermer les modals
      setShowAddressModal(false);

      // 4. Vérifier le mode de paiement
      if (isManualParcelMode) {
        toast.success('Informations de livraison mises à jour');
        setIsRegeneratingParcel(false);
        setShowOrderModal(false);

        setTimeout(() => {
          setIsBatchSelectionMode(true);
          setSelectedOrderIds(new Set([selectedOrder.id!]));
          setShowBatchParcelModal(true);
        }, 300);
        return;
      }

      // 5. Stripe Connect - Régénérer directement
      const response = await fetch(`${FUNCTIONS_BASE_URL}/regenerateSendcloudParcel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: selectedOrder.id })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to regenerate parcel');

      toast.success('Informations de livraison mises à jour et colis régénéré');
      await refreshOrders();
      setShowOrderModal(false);

    } catch (error: any) {
      console.error('Error updating/regenerating:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setIsRegeneratingParcel(false);
    }
  };



  // Handler pour le changement de date range
  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    const normalizedStart = start ? new Date(start) : null;
    const normalizedEnd = end ? new Date(end) : null;

    if (normalizedStart) normalizedStart.setHours(0, 0, 0, 0);
    if (normalizedEnd) normalizedEnd.setHours(23, 59, 59, 999);

    setStartDate(normalizedStart);
    setEndDate(normalizedEnd);
  };

  // Périodes prédéfinies
  const handlePeriodSelect = (period: string) => {
    const today = new Date();
    const start = new Date();

    switch (period) {
      case 'today': {
        start.setHours(0, 0, 0, 0); // Début de journée
        const endToday = new Date();
        endToday.setHours(23, 59, 59, 999); // Fin de journée
        setStartDate(start);
        setEndDate(endToday);
        break;
      }
      case 'yesterday': {
        start.setDate(today.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        const endYesterday = new Date(start);
        endYesterday.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(endYesterday);
        break;
      }
      case 'last7days': {
        start.setDate(today.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        const endToday7 = new Date();
        endToday7.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(endToday7);
        break;
      }
      case 'last30days': {
        start.setDate(today.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        const endToday30 = new Date();
        endToday30.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(endToday30);
        break;
      }
      case 'thisWeek': {
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start.setDate(today.getDate() + diffToMonday);
        start.setHours(0, 0, 0, 0);
        const endTodayWeek = new Date();
        endTodayWeek.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(endTodayWeek);
        break;
      }
      case 'thisMonth': {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        const endTodayMonth = new Date();
        endTodayMonth.setHours(23, 59, 59, 999);
        setStartDate(start);
        setEndDate(endTodayMonth);
        break;
      }
      case 'lastMonth': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        lastMonth.setHours(0, 0, 0, 0);
        lastDayOfLastMonth.setHours(23, 59, 59, 999);
        setStartDate(lastMonth);
        setEndDate(lastDayOfLastMonth);
        break;
      }
      default:
        break;
    }
  };

  // Composant personnalisé pour afficher les périodes prédéfinies
  const CustomDatePickerInput = React.forwardRef<HTMLDivElement, any>(({ value, onClick }, ref) => {
    // Formater le texte pour n'afficher qu'une seule date si début = fin
    let displayValue = value;
    if (value && value.includes(' - ')) {
      const [start, end] = value.split(' - ');
      if (start === end) {
        displayValue = start; // Afficher uniquement la date si c'est le même jour
      }
    }

    return (
      <div ref={ref} className="relative w-full">
        <input
          type="text"
          value={displayValue}
          onClick={onClick}
          readOnly
          placeholder="Sélectionner une période"
          className="w-full px-3 py-2 pr-10 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light text-sm cursor-pointer text-foreground bg-surface"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <Clock className="h-4 w-4 text-foreground-secondary" />
        </div>
      </div>
    );
  });

  // Composant pour les périodes rapides dans le calendrier (à gauche)
  const PeriodButtons = () => (
    <div className="flex flex-col sm:flex-row">
      <div className="w-full sm:w-44 px-3 py-3 bg-background-tertiary sm:border-r border-b sm:border-b-0 border-border">
        <div className="text-xs font-semibold text-foreground mb-3">Périodes rapides</div>
        <div className="flex flex-row sm:flex-col gap-1.5 flex-wrap sm:flex-nowrap">
          <button
            onClick={() => handlePeriodSelect('today')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => handlePeriodSelect('yesterday')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            Hier
          </button>
          <button
            onClick={() => handlePeriodSelect('last7days')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            7 derniers jours
          </button>
          <button
            onClick={() => handlePeriodSelect('last30days')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            30 derniers jours
          </button>
          <button
            onClick={() => handlePeriodSelect('thisWeek')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            Cette semaine
          </button>
          <button
            onClick={() => handlePeriodSelect('thisMonth')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            Ce mois
          </button>
          <button
            onClick={() => handlePeriodSelect('lastMonth')}
            className="flex-1 sm:flex-none px-3 py-2 text-xs text-left bg-surface border border-border rounded hover:bg-primary-10 hover:border-primary text-foreground-secondary font-medium transition-colors whitespace-nowrap"
          >
            Mois dernier
          </button>
        </div>
      </div>
    </div>
  );

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    totalAmount: 0,
    noTracking: 0, // Commandes payées sans tracking
    preparation: {
      toPrepare: 0,
      inProgress: 0,
      ready: 0
    }
  });

  // Function to apply filters and calculate stats
  const applyFiltersAndCalculateStats = (allOrders: FirebaseOrder[]) => {
    let filtered = allOrders;

    if (searchTerm) {
      filtered = filtered.filter(order => {
        const searchLower = searchTerm.toLowerCase();
        return (
          order.orderInfo.email.toLowerCase().includes(searchLower) ||
          order.orderInfo.firstName.toLowerCase().includes(searchLower) ||
          order.orderInfo.lastName.toLowerCase().includes(searchLower) ||
          order.id?.toLowerCase().includes(searchLower) ||
          order.products?.some((product: Product) =>
            product.reference.toLowerCase().includes(searchLower) ||
            product.tiktokUsername.toLowerCase().includes(searchLower) ||
            product.model?.toLowerCase().includes(searchLower)
          )
        );
      });
    }

    if (!statusFilter.includes('all') && statusFilter.length > 0) {
      filtered = filtered.filter(order => {
        return statusFilter.some(filter => {
          if (filter === 'en_cours') {
            return order.products?.some(p => p.paymentInfo.status === 'pending');
          } else if (filter === 'paid') {
            return isOrderPaid(order);
          } else if (filter === 'no_tracking') {
            return orderHasParcelProblem(order);
          } else if (filter === 'to_generate') {
            return orderNeedsParcelGeneration(order);
          } else if (filter === 'todo') {
            return isOrderPaid(order) && (order.preparationStatus === 'todo' || !order.preparationStatus);
          } else if (filter === 'in_progress') {
            return isOrderPaid(order) && order.preparationStatus === 'in_progress';
          } else if (filter === 'ready') {
            return isOrderPaid(order) && order.preparationStatus === 'ready';
          } else if (filter === 'cancelled') {
            return order.status === 'cancelled';
          } else if (filter === 'refunded') {
            return order.products?.some(p =>
              p.paymentInfo.status === 'refunded_full' ||
              p.paymentInfo.status === 'refunded_partial'
            ) || isShippingRefunded(order);
          } else {
            return order.status === filter;
          }
        });
      });
    }

    // Date filtering
    if (startDate || endDate) {
      filtered = filtered.filter(order => {
        if (!order.createdAt) return false;

        let orderDate: Date;
        if (order.createdAt && 'toDate' in order.createdAt) {
          orderDate = order.createdAt.toDate();
        } else if (order.createdAt instanceof Date) {
          orderDate = order.createdAt;
        } else {
          // Handle other possible date formats
          orderDate = new Date(order.createdAt as any);
        }

        // Comparer les dates (en ignorant les heures)
        const orderDateOnly = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());

        if (startDate) {
          const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          if (orderDateOnly < startDateOnly) return false;
        }

        if (endDate) {
          const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
          if (orderDateOnly > endDateOnly) return false;
        }

        return true;
      });
    }

    // Calculate stats from filtered orders
    const newStats = filtered.reduce((acc, order) => {
      const paidProducts = order.products?.filter(p => p.paymentInfo.status === 'paid') || [];
      const paidAmount = paidProducts.reduce((sum, p) => sum + Number.parseFloat(p.amount || '0'), 0);

      // Add delivery cost only if there's at least one paid product and delivery is paid
      const deliveryCost = paidProducts.length > 0 && order.deliveryInfo.paymentStatus === 'paid' ?
        (order.deliveryInfo.shippingCost || 0) : 0;

      // Calculate preparation stats for paid orders only
      let preparationStats = { ...acc.preparation };
      if (isOrderPaid(order)) {
        // Console log pour debug
        console.log('Order preparation debug:', {
          orderId: order.id,
          preparationStatus: order.preparationStatus,
          type: typeof order.preparationStatus
        });

        if (order.preparationStatus === 'todo' || !order.preparationStatus) {
          preparationStats.toPrepare++;
        } else if (order.preparationStatus === 'in_progress') {
          preparationStats.inProgress++;
        } else if (order.preparationStatus === 'ready') {
          preparationStats.ready++;
        }
      }

      return {
        total: acc.total + 1,
        pending: acc.pending + (order.status === 'pending_payment' || order.status === 'draft' ? 1 : 0),
        paid: acc.paid + (isOrderPaid(order) ? 1 : 0),
        noTracking: acc.noTracking + (orderHasParcelProblem(order) ? 1 : 0),
        totalAmount: acc.totalAmount + paidAmount + deliveryCost,
        preparation: preparationStats
      };
    }, {
      total: 0,
      pending: 0,
      paid: 0,
      totalAmount: 0,
      noTracking: 0,
      preparation: {
        toPrepare: 0,
        inProgress: 0,
        ready: 0
      }
    });

    setFilteredOrders(filtered);
    setStats(newStats);
  };

  const normalizedStartEnd = useCallback(() => {
    const start = startDate ? new Date(startDate) : (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
    const end = endDate ? new Date(endDate) : (() => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; })();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [startDate, endDate]);

  // Subscription effect for real-time order updates
  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    if (!idorga) return;

    // Setup subscription
    const { start, end } = normalizedStartEnd();
    setLoading(true);

    console.log('📡 Subscribing to orders for range:', start, end);

    const unsubscribe = OrderRepository.subscribeToOrdersByDateRange(start, end, (newOrders) => {
      console.log(`📥 Received ${newOrders.length} orders in real-time update`);
      setOrders(newOrders);
      setLoading(false);

      // Initialize expanded orders if needed
      if (newOrders.length > 0) {
        const ids = new Set(newOrders.map(o => o.id).filter(Boolean) as string[]);
        setExpandedOrders(ids);
      }
    });

    return () => {
      console.log('Unsubscribing from orders');
      unsubscribe();
    };
  }, [currentUser, navigate, idorga, normalizedStartEnd]);

  // Compatibility: Manual refresh is no-op in real-time mode
  const refreshOrders = useCallback(async () => {
    console.log('🔄 Dashboard is in real-time mode (refresh is auto)');
  }, []);


  useEffect(() => {
    applyFiltersAndCalculateStats(orders);
  }, [orders, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Calcul de la pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Toggle order expansion
  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Expand all orders (de la page courante)
  const expandAllOrders = () => {
    const allOrderIds = new Set(paginatedOrders.map(order => order.id!).filter(Boolean));
    setExpandedOrders(allOrderIds);
  };

  // Collapse all orders
  const collapseAllOrders = () => {
    setExpandedOrders(new Set());
  };

  // Helper: Détermine si une commande est payée (produits et/ou frais de livraison)
  const isOrderPaid = (order: FirebaseOrder): boolean => {
    const hasPaidProduct = order.products?.some(p => p.paymentInfo?.status === 'paid') ?? false;
    const hasPaidShipping = order.deliveryInfo?.paymentStatus === 'paid';
    return hasPaidProduct || hasPaidShipping;
  };

  // Livraison remboursée : basé sur les statuts (backend / Sendcloud) ou remboursement total de la commande
  const isShippingRefunded = (order: FirebaseOrder): boolean => {
    if (!order.deliveryInfo?.shippingCost || order.deliveryInfo.shippingCost <= 0) {
      return false;
    }

    const di = order.deliveryInfo as { paymentStatus?: string; sendcloudRefund?: { status?: string; shippingRefundAmount?: number } };

    // 1) Statut livraison marqué remboursé côté backend
    if (di.paymentStatus === 'refunded') {
      return true;
    }

    // 2) Sendcloud : remboursement label terminé avec montant livraison remboursé
    const scr = di.sendcloudRefund;
    const sendcloudCompleted = scr?.status === 'completed' || scr?.status === 'completed_refund' || scr?.status === 'comple';
    if (scr && sendcloudCompleted && (scr.shippingRefundAmount ?? 0) > 0) {
      return true;
    }

    // 3) Tous les produits sont remboursés totalement → la livraison est considérée remboursée
    const products = order.products ?? [];
    if (products.length > 0 && products.every(p => p.paymentInfo?.status === 'refunded_full')) {
      return true;
    }

    return false;
  };

  /** Reste à rembourser pour un produit (€). Produit 0 = produit + livraison - déjà remboursé ; autres = produit - déjà remboursé. */
  const getRemainingRefundableForProduct = (order: FirebaseOrder, productIndex: number): number => {
    const product = order.products?.[productIndex];
    if (!product) return 0;
    const productAmount = Number.parseFloat(product.amount || '0') || 0;
    const alreadyRefundedCents = product.amountRefunded ?? 0;
    const alreadyRefunded = typeof alreadyRefundedCents === 'number' ? alreadyRefundedCents / 100 : Number.parseFloat(String(alreadyRefundedCents)) / 100;
    const shippingCost = productIndex === 0 ? (order.deliveryInfo?.shippingCost ?? 0) : 0;
    const totalMax = productAmount + shippingCost;
    return Math.max(0, totalMax - alreadyRefunded);
  };

  /** Statut affiché pour un produit : pour le produit 0, "refunded_full" seulement si tout est remboursé OU si le montant remboursé = exactement le prix produit (produit remboursé, livraison non). Pas "total" si seul la livraison est remboursée. */
  const getProductDisplayStatus = (
    order: FirebaseOrder,
    product: Product,
    index: number
  ): 'pending' | 'paid' | 'failed' | 'canceled' | 'disputed' | 'refunded_partial' | 'refunded_full' => {
    const status = product.paymentInfo?.status;
    if (status !== 'refunded_partial' || index !== 0) return status as any;
    const productAmountCents = Math.round((Number.parseFloat(product.amount || '0') || 0) * 100);
    const shippingCents = Math.round((order.deliveryInfo?.shippingCost ?? 0) * 100);
    const totalCents = productAmountCents + shippingCents;
    const amountRefunded = product.amountRefunded ?? 0;
    if (amountRefunded >= totalCents) return 'refunded_full';
    if (amountRefunded === productAmountCents) return 'refunded_full';
    return 'refunded_partial';
  };

  // Helper: colis annoncé / suivi / étiquette disponible (dateAnnounced, trackingUrl ou labelPrinterUrl)
  const getOrderTrackingUrl = (order: FirebaseOrder): string | null =>
    order.parcelGenerated?.trackingUrl || null;

  const getOrderLabelUrl = (order: FirebaseOrder): string | null =>
    order.parcelGenerated?.labelPrinterUrl || null;

  const hasTrackingOrLabel = (order: FirebaseOrder): boolean =>
    !!(order.parcelGenerated?.dateAnnounced || getOrderTrackingUrl(order) || getOrderLabelUrl(order));

  const isInsufficientCreditsParcelError = (order: FirebaseOrder): boolean => {
    const parcelError = order.deliveryInfo?.parcelCreationError;
    const msg = String(parcelError?.message || '').toLowerCase();
    return parcelError?.code === 'INSUFFICIENT_CREDITS' ||
      msg.includes('crédits insuffisants') ||
      msg.includes('insufficient credits');
  };

  /** Erreur explicite « générez manuellement » — pas un échec API Sendcloud. */
  const isManualOnlyParcelError = (order: FirebaseOrder): boolean => {
    const msg = String(order.deliveryInfo?.parcelCreationError?.message || '').toLowerCase();
    return msg.includes('création auto désactivée') ||
      msg.includes('creation auto desactivee') ||
      msg.includes('générez les colis') ||
      msg.includes('generez les colis');
  };

  const hasRealParcelError = (order: FirebaseOrder): boolean => {
    const parcelError = order.deliveryInfo?.parcelCreationError;
    if (!parcelError) return false;
    if (isInsufficientCreditsParcelError(order)) return false;
    if (isManualOnlyParcelError(order)) return false;
    return true;
  };

  const isManualGenerationPending = (order: FirebaseOrder): boolean => {
    const isPaid = isOrderPaid(order);
    const hasParcelGenerated = Boolean(order.parcelGenerated);
    if (!isPaid || hasParcelGenerated) return false;
    // Échec API / métier (ex. factures Sendcloud impayées) → resolver, pas génération batch
    if (hasRealParcelError(order)) return false;
    return isManualParcelMode ||
      isInsufficientCreditsParcelError(order) ||
      isManualOnlyParcelError(order);
  };

  // Helper: Vérifie si une commande nécessite la génération de colis (pas encore d'annonce/tracking/label)
  const orderNeedsParcelGeneration = (order: FirebaseOrder): boolean => {
    const isPaid = isOrderPaid(order);
    const hasTrackingOrLabelNow = hasTrackingOrLabel(order);
    const hasDeliveryMethod = !!resolveDashboardShippingMethodId(order.deliveryInfo);
    const hasParcelGenerated = Boolean(order.parcelGenerated);
    if (hasRealParcelError(order)) return false;
    return isPaid && !hasTrackingOrLabelNow && hasDeliveryMethod && !hasParcelGenerated;
  };

  // Helper: Problème parcel = payée, sans tracking, et pas simplement "à générer" (exclut les parcels à générer des alertes)
  const orderHasParcelProblem = (order: FirebaseOrder): boolean =>
    isOrderPaid(order) && !hasTrackingOrLabel(order) && !orderNeedsParcelGeneration(order);

  // Gestion de la multi-sélection
  const toggleOrderSelection = (orderId: string) => {
    const newSelected = new Set(selectedOrderIds);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrderIds(newSelected);
  };

  const selectAllEligibleOrders = () => {
    const eligibleOrderIds = paginatedOrders
      .filter(order => orderNeedsParcelGeneration(order))
      .map(order => order.id!)
      .filter(Boolean);
    setSelectedOrderIds(new Set(eligibleOrderIds));
  };

  const deselectAllOrders = () => {
    setSelectedOrderIds(new Set());
  };

  // Activer le mode de sélection batch et sélectionner toutes les commandes éligibles
  const enterBatchSelectionMode = () => {
    // Appliquer le filtre "to_generate" pour ne montrer que les commandes à générer
    // tout en conservant les autres filtres actifs (recherche, dates, etc.)
    if (!statusFilter.includes('to_generate')) {
      const currentFilters = statusFilter.filter(s => s !== 'all');
      setStatusFilter(currentFilters.length > 0 ? [...currentFilters, 'to_generate'] : ['to_generate']);
    }
    setIsBatchSelectionMode(true);
    selectAllEligibleOrders();
  };

  // Quitter le mode de sélection batch
  const exitBatchSelectionMode = () => {
    setIsBatchSelectionMode(false);
    deselectAllOrders();
  };

  // Obtenir les commandes sélectionnées
  const selectedOrders = paginatedOrders.filter(order =>
    order.id && selectedOrderIds.has(order.id)
  );

  // Compter les commandes éligibles pour la génération de colis
  const eligibleOrdersCount = paginatedOrders.filter(orderNeedsParcelGeneration).length;

  // Get payment status color for products
  const getPaymentStatusColor = (status: 'pending' | 'paid' | 'failed' | 'canceled' | 'disputed' | 'refunded_partial' | 'refunded_full') => {
    switch (status) {
      case 'pending': return 'bg-warning-10 text-warning-dark';
      case 'paid': return 'bg-success-10 text-success-dark';
      case 'failed': return 'bg-error-10 text-error-dark';
      case 'canceled': return 'bg-background-tertiary text-foreground-secondary';
      case 'disputed': return 'bg-warning-10 text-warning-dark';
      case 'refunded_partial': return 'bg-warning-10 text-warning-dark';
      case 'refunded_full': return 'bg-error-10 text-error-dark';
      default: return 'bg-background-tertiary text-foreground-secondary';
    }
  };

  // Get payment status text
  const getPaymentStatusText = (status: 'pending' | 'paid' | 'failed' | 'canceled' | 'disputed' | 'refunded_partial' | 'refunded_full') => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'paid': return 'Payé';
      case 'failed': return 'Échoué';
      case 'canceled': return 'Annulé';
      case 'disputed': return 'Litige';
      case 'refunded_partial': return 'Remboursé (partiel)';
      case 'refunded_full': return 'Remboursé (total)';
      default: return status;
    }
  };

  // Get payment provider info
  // Se base sur la présence de transferId/destinationPaymentId pour déterminer le mode:
  // - Stripe Connect: utilise transfer_data.destination donc aura transferId et/ou destinationPaymentId
  // - Stripe API Key: pas de transfert, l'argent va directement au compte du vendeur
  const getPaymentProviderInfo = (method?: 'stripe' | 'paypal' | 'stripe_connect' | 'stripe_api_key' | string, product?: Product): {
    name: string;
    badgeVariant: 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'paypal' | 'stripe' | 'stripe-api';
    icon: typeof CreditCard | typeof Wallet;
    idLabel: string;
    idLabelShort: string;
  } => {
    if (method === 'paypal') {
      return {
        name: 'PayPal',
        badgeVariant: 'paypal', // Bleu PayPal officiel
        icon: Wallet,
        idLabel: 'ID de transaction PayPal',
        idLabelShort: 'TX'
      };
    }

    // Gestion des nouveaux modes explicites
    if (method === 'stripe_connect') {
      return {
        name: 'Stripe Connect',
        badgeVariant: 'stripe', // Violet Stripe officiel
        icon: CreditCard,
        idLabel: 'ID de transaction Stripe Connect (PI)',
        idLabelShort: 'PI'
      };
    }

    if (method === 'stripe_api_key') {
      return {
        name: 'Stripe API Key',
        badgeVariant: 'stripe-api', // Indigo pour différencier de Stripe Connect
        icon: CreditCard,
        idLabel: 'ID de transaction Stripe (PI)',
        idLabelShort: 'PI'
      };
    }

    // Fallback Legacy (si method === 'stripe')
    // Vérifier si c'est Stripe Connect (présence de transferId ou destinationPaymentId)
    const isStripeConnect = product?.paymentInfo?.method === 'stripe' &&
      (product.paymentInfo.transferId || product.paymentInfo.destinationPaymentId);

    if (isStripeConnect) {
      return {
        name: 'Stripe Connect',
        badgeVariant: 'stripe', // Violet Stripe officiel
        icon: CreditCard,
        idLabel: 'ID de transaction Stripe Connect (PI)',
        idLabelShort: 'PI'
      };
    }

    // Sinon par défaut c'est probable Stripe API Key (ou ancien paiement direct)
    return {
      name: 'Stripe API Key',
      badgeVariant: 'stripe-api', // Indigo pour différencier de Stripe Connect
      icon: CreditCard,
      idLabel: 'ID de transaction Stripe (PI)',
      idLabelShort: 'PI'
    };
  };

  // Get order status color
  const getOrderStatusColor = (status: FirebaseOrder['status']) => {
    switch (status) {
      case 'draft':
      case 'pending_payment': return 'bg-warning-10 text-warning-dark';
      case 'paid': return 'bg-success-10 text-success-dark';
      case 'cancelled': return 'bg-error-10 text-error-dark';
      default: return 'bg-background-tertiary text-foreground-secondary';
    }
  };

  // Get preparation status color
  const getPreparationStatusColor = (status?: 'todo' | 'in_progress' | 'ready') => {
    switch (status) {
      case 'todo': return 'bg-warning-10 text-warning-dark';
      case 'in_progress': return 'bg-primary-10 text-primary-dark';
      case 'ready': return 'bg-success-10 text-success-dark';
      default: return 'bg-warning-10 text-warning-dark'; // null/undefined considéré comme todo
    }
  };

  const getStatusText = (status: FirebaseOrder['status']) => {
    switch (status) {
      case 'draft':
      case 'pending_payment': return 'En cours';
      case 'paid': return 'Payé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const updateProductPaymentStatus = async (orderId: string, productIndex: number, status: 'pending' | 'paid' | 'failed') => {
    try {
      await OrderRepository.updatePaymentStatus(orderId, productIndex, status);
      toast.success(t('common.success'));
      // Mettre à jour la commande affichée dans la modale pour refléter le nouveau statut
      setSelectedOrder((prev) => {
        if (!prev || prev.id !== orderId) return prev;
        const products = [...(prev.products || [])];
        if (!products[productIndex]) return prev;
        const now = new Date().toISOString();
        products[productIndex] = {
          ...products[productIndex],
          paymentInfo: {
            ...products[productIndex].paymentInfo,
            status,
            ...(status === 'paid' && { paidAt: now }),
            ...(status === 'failed' && { failedAt: now }),
          },
        };
        return { ...prev, products };
      });
    } catch (error) {
      console.error('Error updating product payment status:', error);
      toast.error(t('common.error'));
      throw error;
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmationAction) return;

    try {
      await updateProductPaymentStatus(
        confirmationAction.orderId,
        confirmationAction.productIndex,
        confirmationAction.type
      );
      setShowConfirmation(false);
      setConfirmationAction(null);
    } catch (error) {
      // Error is already handled in updateProductPaymentStatus
    }
  };

  const showPaymentConfirmation = (orderId: string, productIndex: number, type: 'paid' | 'failed', productReference: string) => {
    setConfirmationAction({ type, orderId, productIndex, productReference });
    setShowConfirmation(true);
  };

  const updateOrderStatus = async (orderId: string, status: FirebaseOrder['status']) => {
    try {
      await OrderRepository.updateOrderStatus(orderId, status);
      toast.success(t('common.success'));

      // Refresh orders to get updated data
      await refreshOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(t('common.error'));
    }
  };



  const updateOrderPreparationStatus = async (orderId: string, status: 'todo' | 'in_progress' | 'ready') => {
    try {
      await OrderRepository.updateOrderPreparationStatus(orderId, status);
      toast.success(t('common.success'));

      // Refresh orders to get updated data
      await refreshOrders();
    } catch (error) {
      console.error('Error updating order preparation status:', error);
      toast.error(t('common.error'));
    }
  };

  const printLabel = async (order: FirebaseOrder) => {
    const labelUrl = getOrderLabelUrl(order);
    if (!labelUrl) {
      toast.error(t('common.error'));
      return;
    }

    try {
      console.log('🖨️ Printing shipping label for order:', order.id);

      // Use the same Firebase Cloud Function for fetching labels
      // This works in both development and production
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadShippingLabel?orderId=${order.id}`;

      console.log('📡 Calling Cloud Function for printing:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch label: ${response.status}`);
      }

      const blob = await response.blob();

      // Marquer l'étiquette comme imprimée (dès que le PDF est récupéré)
      if (order.id) {
        try {
          await OrderRepository.markLabelPrinted(order.id);
          // Refresh orders to get updated data
          await refreshOrders();
        } catch (e) {
          // Ne bloque pas l'impression si l'update échoue
          console.warn('⚠️ Impossible de marquer labelPrinted:', e);
        }
      }

      // Create a temporary URL for the PDF blob
      const url = globalThis.URL.createObjectURL(blob);

      // Open the PDF in a new window/tab for printing
      const printWindow = globalThis.open(url, '_blank', 'width=800,height=600');

      if (printWindow) {
        toast.success(t('common.success'));

        // Wait for the PDF to load, then trigger print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000); // Give more time for PDF to load
        };

        // Fallback: trigger print after a longer delay if onload doesn't work
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.print();
          }
        }, 2000);

        // Clean up the URL when the window is closed (user's responsibility to close)
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            globalThis.URL.revokeObjectURL(url);
            clearInterval(checkClosed);
            console.log('✅ Print window closed, URL cleaned up');
          }
        }, 1000);

      } else {
        // Fallback: if popup is blocked, create a download link instead
        const link = document.createElement('a');
        link.href = url;
        link.download = `shipping-label-ORDER_${order.id?.slice(-8) || 'UNKNOWN'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        toast.success(t('common.success'));
        return;
      }

      console.log('✅ Label sent to printer successfully');
    } catch (error) {
      console.error('❌ Error printing label:', error);
      toast.error(t('common.error'));
    }
  };

  const downloadLabel = async (order: FirebaseOrder) => {
    const labelUrl = getOrderLabelUrl(order);
    if (!labelUrl) {
      toast.error(t('common.error'));
      return;
    }

    try {
      console.log('📥 Downloading shipping label for order:', order.id);

      // Use the Firebase Cloud Function for downloading labels
      // This works in both development and production
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadShippingLabel?orderId=${order.id}`;

      console.log('📡 Calling Cloud Function:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch label: ${response.status}`);
      }

      const blob = await response.blob();

      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipping-label-ORDER_${order.id?.slice(-8) || 'UNKNOWN'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      toast.success(t('common.success'));
      console.log('✅ Label downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading label:', error);
      toast.error(t('common.error'));
    }
  };

  const printAllLabels = async () => {
    // Get all orders with paid status that have tracking info
    const ordersWithLabels = filteredOrders.filter(order =>
      isOrderPaid(order) && getOrderLabelUrl(order)
    );

    if (ordersWithLabels.length === 0) {
      toast.error(t('admin.dashboard.stripe.error'));
      return;
    }

    try {
      console.log(`🖨️ Printing ${ordersWithLabels.length} labels...`);
      toast.loading(`Préparation de ${ordersWithLabels.length} étiquettes...`);

      // Extract order IDs
      const orderIds = ordersWithLabels.map(order => order.id).filter(Boolean) as string[];

      // Use the Firebase Cloud Function for bulk label download
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadAllShippingLabels`;

      console.log('📡 Calling bulk Cloud Function:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch labels: ${response.status}`);
      }

      const blob = await response.blob();

      // Marquer toutes les étiquettes comme imprimées (dès que le PDF bulk est récupéré)
      try {
        await OrderRepository.markLabelsPrinted(orderIds);
        await refreshOrders();
      } catch (e) {
        console.warn('⚠️ Impossible de marquer labelPrinted (bulk):', e);
      }

      // Create a temporary URL for the PDF blob
      const url = globalThis.URL.createObjectURL(blob);

      // Open the PDF in a new window/tab for printing
      const printWindow = globalThis.open(url, '_blank', 'width=800,height=600');

      if (printWindow) {
        toast.dismiss();
        toast.success(`Fenêtre d'impression ouverte avec ${ordersWithLabels.length} étiquettes`);

        // Wait for the PDF to load, then trigger print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };

        // Fallback: trigger print after a longer delay if onload doesn't work
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.print();
          }
        }, 2000);

        // Clean up the URL when the window is closed (user's responsibility to close)
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            globalThis.URL.revokeObjectURL(url);
            clearInterval(checkClosed);
            console.log('✅ Print window closed, URL cleaned up');
          }
        }, 1000);

      } else {
        // Fallback: if popup is blocked, create a download link instead
        const link = document.createElement('a');
        link.href = url;
        link.download = `shipping-labels-bulk-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        toast.dismiss();
        toast.success(t('common.success'));
        return;
      }

      console.log('✅ Bulk labels sent to printer successfully');
    } catch (error) {
      console.error('❌ Error printing bulk labels:', error);
      toast.dismiss();
      toast.error('Erreur lors de l\'impression groupée');
    }
  };

  const downloadAllLabels = async () => {
    // Get all orders with paid status that have tracking info
    const ordersWithLabels = filteredOrders.filter(order =>
      isOrderPaid(order) && getOrderLabelUrl(order)
    );

    if (ordersWithLabels.length === 0) {
      toast.error(t('admin.dashboard.stripe.error'));
      return;
    }

    try {
      console.log(`📥 Downloading ${ordersWithLabels.length} labels...`);
      toast.loading(`Téléchargement de ${ordersWithLabels.length} étiquettes...`);

      // Extract order IDs
      const orderIds = ordersWithLabels.map(order => order.id).filter(Boolean) as string[];

      // Use the Firebase Cloud Function for bulk label download
      const functionUrl = `${FUNCTIONS_BASE_URL}/downloadAllShippingLabels`;

      console.log('📡 Calling bulk Cloud Function:', functionUrl);

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch labels: ${response.status}`);
      }

      const blob = await response.blob();

      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipping-labels-bulk-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success(`${ordersWithLabels.length} étiquettes téléchargées`);
      console.log('✅ Bulk labels downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading bulk labels:', error);
      toast.dismiss();
      toast.error('Erreur lors du téléchargement groupé');
    }
  };

  const handleExportCsv = () => {
    try {
      exportOrdersToCsv(filteredOrders);
      toast.success(t('common.success'));
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      toast.error(t('common.error'));
    }
  };

  const showCreditRefundModal = (order: FirebaseOrder, productIndex: number = 0) => {
    setCreditOrder(order);
    setCreditProductIndex(productIndex);
    const product = order.products?.[productIndex];
    const productAmount = product ? Number.parseFloat(product.amount || '0') : 0;
    const alreadyRefundedCents = product?.amountRefunded ?? 0;
    const alreadyRefunded = typeof alreadyRefundedCents === 'number'
      ? alreadyRefundedCents / 100
      : Number.parseFloat(String(alreadyRefundedCents)) / 100;
    const maxRefundableProduct = Math.max(0, productAmount - alreadyRefunded);
    const shippingCost = order.deliveryInfo?.shippingCost ?? 0;
    const hasOneProduct = order.products?.length === 1;
    const canIncludeShipping = hasOneProduct && shippingCost > 0 && !order.deliveryInfo?.sendcloudRefund;
    const totalWithShipping = productAmount + shippingCost;
    const maxRefundableWithShipping = Math.max(0, totalWithShipping - alreadyRefunded);

    // Si le produit est déjà entièrement remboursé mais qu'il reste la livraison, pré-cocher "inclure livraison" pour activer le bouton
    if (maxRefundableProduct <= 0 && canIncludeShipping && maxRefundableWithShipping > 0) {
      setIncludeShipping(true);
      setCreditAmount(maxRefundableWithShipping.toFixed(2));
    } else {
      setIncludeShipping(false);
      setCreditAmount(maxRefundableProduct > 0 ? maxRefundableProduct.toFixed(2) : (productAmount > 0 ? productAmount.toFixed(2) : ''));
    }
    setCreditDescription(`Remboursement pour commande ${order.id?.slice(-8) || ''}`);
    setShowCreditModal(true);
  };

  const openFullRefundModal = (order: FirebaseOrder) => {
    setFullRefundOrder(order);
    const remainingTotal = (order.products?.length ?? 0) > 0
      ? order.products!.reduce(
          (sum: number, _p: Product, i: number) => sum + getRemainingRefundableForProduct(order, i),
          0
        )
      : 0;
    const shippingCost = order.deliveryInfo?.shippingCost ?? 0;
    const hasSendcloudRefund = !!order.deliveryInfo?.sendcloudRefund;
    const canTryShipping = shippingCost > 0 && !hasSendcloudRefund;
    const product0 = order.products?.[0];
    const amountRefunded0 = (product0?.amountRefunded ?? 0) / 100;
    const product0Amount = Number.parseFloat(product0?.amount || '0') || 0;
    const onlyShippingLeft =
      remainingTotal > 0 &&
      canTryShipping &&
      remainingTotal <= shippingCost + 1e-6 &&
      product0?.paymentInfo?.status === 'refunded_partial' &&
      amountRefunded0 >= product0Amount;
    setFullRefundIncludeShipping(!!onlyShippingLeft);
    setShowFullRefundModal(true);
  };

  // Rembourser toute la commande (tous les produits payés + éventuellement les frais de livraison)
  const handleFullOrderRefund = async (order: FirebaseOrder, includeShippingRequested: boolean) => {
    // Éviter les doubles clics
    if (processingRefund) {
      toast.error('Un remboursement est déjà en cours, veuillez patienter...');
      return;
    }

    try {
      setProcessingRefund(true);

      // Produits à rembourser : payés ou partiellement remboursés avec un reste
      const paidProductIndices: number[] = [];
      order.products?.forEach((product, index) => {
        if (product.paymentInfo?.status === 'paid' && product.paymentInfo?.paymentIntentId) {
          paidProductIndices.push(index);
        } else if (
          product.paymentInfo?.status === 'refunded_partial' &&
          product.paymentInfo?.paymentIntentId &&
          getRemainingRefundableForProduct(order, index) > 0
        ) {
          paidProductIndices.push(index);
        }
      });

      if (paidProductIndices.length === 0) {
        throw new Error('Aucun produit à rembourser pour cette commande.');
      }

      const refundResults: { productIndex: number; success: boolean; error?: string }[] = [];

      // Refund each paid product — inclure la livraison uniquement sur le produit d'index 0 (celui qui porte le paiement livraison en Stripe Connect)
      for (let i = 0; i < paidProductIndices.length; i++) {
        const productIndex = paidProductIndices[i];

        try {
          const response = await fetch(`${FUNCTIONS_BASE_URL}/refundOrderWithDebtAdjustments`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId: order.id,
              productIndex,
              includeShipping: productIndex === 0 && includeShippingRequested, // Livraison uniquement sur le produit 0
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            refundResults.push({
              productIndex,
              success: false,
              error: errorData?.error || `Erreur lors du remboursement du produit ${productIndex}`,
            });
          } else {
            refundResults.push({ productIndex, success: true });
          }
        } catch (err) {
          refundResults.push({
            productIndex,
            success: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      const successCount = refundResults.filter(r => r.success).length;
      const failedCount = refundResults.filter(r => !r.success).length;

      if (successCount === 0) {
        throw new Error('Aucun remboursement n\'a pu être effectué.');
      }

      const message = failedCount > 0
        ? `Remboursement partiel: ${successCount} produit(s) remboursé(s), ${failedCount} échec(s)`
        : `Remboursement complet effectué pour ${successCount} produit(s)`;

      toast.success(message, { duration: 7000 });

      // Rafraîchir les commandes pour voir les nouveaux statuts
      await refreshOrders();
    } catch (error) {
      console.error('Erreur lors du remboursement complet de la commande:', error);
      toast.error(error instanceof Error ? error.message : t('admin.dashboard.credit.error'));
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleDirectRefund = async () => {
    // PROTECTION CONTRE LES DOUBLES CLICS : Désactiver immédiatement
    if (processingRefund) {
      toast.error('Un remboursement est déjà en cours, veuillez patienter...');
      return;
    }

    if (!creditOrder || !creditAmount) {
      toast.error(t('admin.dashboard.credit.error'));
      return;
    }

    const amount = Number.parseFloat(creditAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error('Le montant doit être supérieur à 0');
      return;
    }

    const product = creditOrder.products?.[creditProductIndex];
    if (!product) {
      toast.error('Produit non trouvé');
      return;
    }

    // Vérifier que le produit a un paymentIntentId
    if (!product.paymentInfo?.paymentIntentId) {
      toast.error('Impossible de trouver le paiement pour ce produit. Le remboursement direct nécessite un paiement Stripe ou PayPal.');
      return;
    }

    // Calculer le montant total remboursable (produit + frais de livraison si inclus)
    const productAmount = Number.parseFloat(product.amount || '0');
    const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
    const totalAmount = productAmount + shippingCost;

    // Vérifier le montant déjà remboursé si disponible
    // amountRefunded est stocké en centimes dans Firestore
    const alreadyRefundedInCents = product.amountRefunded || 0;
    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
      ? alreadyRefundedInCents / 100
      : Number.parseFloat((alreadyRefundedInCents as any).toString()) / 100;

    // Le montant maximum remboursable dépend si on inclut les frais de livraison ou non
    const maxRefundable = includeShipping
      ? totalAmount - alreadyRefunded  // Produit + livraison - déjà remboursé
      : productAmount - alreadyRefunded; // Seulement produit - déjà remboursé

    // Validation côté client: vérifier que le montant ne dépasse pas le montant disponible
    if (amount > totalAmount) {
      toast.error(
        `Le montant demandé (€${amount.toFixed(2)}) ne peut pas dépasser le montant total ${includeShipping ? '(produit + livraison)' : 'du produit'} (€${totalAmount.toFixed(2)})`
      );
      return;
    }

    // Vérification stricte : le montant ne peut pas dépasser le montant restant remboursable
    if (amount > maxRefundable) {
      toast.error(
        `Le montant demandé (€${amount.toFixed(2)}) dépasse le montant restant remboursable (€${maxRefundable.toFixed(2)}). ` +
        `Montant ${includeShipping ? 'total (produit + livraison)' : 'du produit'}: €${totalAmount.toFixed(2)}, déjà remboursé: €${alreadyRefunded.toFixed(2)}`
      );
      return;
    }

    // Vérification supplémentaire : s'assurer qu'il reste quelque chose à rembourser
    if (maxRefundable <= 0) {
      toast.error(includeShipping
        ? 'Ce produit et les frais de livraison ont déjà été entièrement remboursés.'
        : 'Ce produit a déjà été entièrement remboursé.'
      );
      return;
    }

    // Vérification finale : le montant doit être strictement positif et inférieur ou égal au montant restant
    if (amount <= 0 || amount > maxRefundable) {
      toast.error(`Le montant doit être compris entre €0.01 et €${maxRefundable.toFixed(2)}`);
      return;
    }

    try {
      // Activer le flag de traitement AVANT l'appel API pour éviter les doubles clics
      setProcessingRefund(true);

      // Appeler l'API de remboursement Stripe avec paymentIntentId
      // La fonction backend récupérera automatiquement le chargeId depuis le PaymentIntent
      // Le backend validera également le montant pour éviter les fraudes
      const response = await fetch(`${FUNCTIONS_BASE_URL}/refundProduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId: product.paymentInfo.paymentIntentId,
          organizationId: selectedOrder?.organizationId || configuration?.organizationId,
          amount,
          reason: creditDescription || `Remboursement direct pour commande ${creditOrder.id?.slice(-8) || ''}`,
          orderId: creditOrder.id,
          productIndex: creditProductIndex,
          includeShipping: includeShipping,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors du remboursement');
      }

      const result = await response.json();

      // Construire le message de succès (internationalisé)
      let refundMessage = t('admin.dashboard.refund.successCard', { amount: amount.toFixed(2) });
      if (includeShipping) {
        refundMessage += `\n\n${t('admin.dashboard.refund.shippingTried')}`;
      }

      // Ajouter l'info sur le remboursement du label transporteur si présent
      if (result.data?.sendcloudLabelRefund) {
        const labelRefund = result.data.sendcloudLabelRefund;
        if (labelRefund.success) {
          refundMessage += `\n\n${t('admin.dashboard.refund.shippingLabelSuccess')}`;
        } else {
          refundMessage += `\n\n${t('admin.dashboard.refund.shippingLabelFailed', { message: labelRefund.message || '' })}`;
        }
      }

      toast.success(refundMessage, { duration: 6000 });

      setShowCreditModal(false);
      setCreditOrder(null);
      setCreditAmount('');
      setCreditDescription('');
      setIncludeShipping(false);

      // Rafraîchir les commandes pour voir le nouveau statut
      await refreshOrders();
    } catch (error) {
      console.error('Erreur lors du remboursement direct:', error);
      toast.error(error instanceof Error ? error.message : t('admin.dashboard.credit.error'));
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleRefundSubmit = async () => {
    await handleDirectRefund();
  };

  const openOrderModal = (order: FirebaseOrder) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Ouvrir le Dashboard Express Stripe
  const handleOpenExpressDashboard = async () => {
    const stripeAccountId = configuration?.stripeConnectConfig?.accountId;

    if (!stripeAccountId) {
      console.error('❌ Aucun compte Stripe configuré');
      return;
    }

    setIsOpeningDashboard(true);
    try {
      await StripeExpressDashboardService.openExpressDashboard(stripeAccountId);
    } finally {
      setIsOpeningDashboard(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (typeof timestamp === 'string' && isNaN(date.getTime())) {
        return timestamp;
      }

      // Format DD/MM/YYYY HH:MM
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return typeof timestamp === 'string' ? timestamp : 'Date invalide';
    }
  };

  /** Retourne le failureCode (court) pour affichage non envahissant. */
  const getLatestFailureCode = (p: Product): string | undefined => {
    const attempts: any[] | undefined = (p as any)?.paymentInfo?.paymentAttempts;
    if (attempts && Array.isArray(attempts) && attempts.length > 0) {
      const getTime = (t: any) => {
        if (!t) return 0;
        if (t.seconds) return t.seconds * 1000;
        if (t.toMillis) return t.toMillis();
        if (typeof t === 'string') {
          const d = new Date(t);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        }
        return 0;
      };
      const failed = [...attempts]
        .sort((a, b) => getTime(b.timestamp) - getTime(a.timestamp))
        .find((a: any) => a?.status === 'failed' && (a?.failureCode || a?.failureReason));
      if (failed?.failureCode) return failed.failureCode as string;
      if (failed?.failureReason) return failed.failureReason as string;
    }
    const info: any = (p as any)?.paymentInfo;
    return (info?.failureCode || info?.failureReason) as string | undefined;
  };

  // Vérifier si au moins une commande a une dette
  const hasAnyDebt = filteredOrders.some((order: any) =>
    order.debtHistory && Array.isArray(order.debtHistory) && order.debtHistory.length > 0
  );

  const getReturnStatusBadge = (status?: string | null) => {
    if (!status || status === 'none') return null;

    let colorClass = 'bg-background-tertiary text-foreground-secondary';
    let label = status;

    switch (status) {
      case 'requested':
        colorClass = 'bg-warning-10 text-warning-dark';
        label = 'Retour demandé';
        break;
      case 'in_transit':
        colorClass = 'bg-primary-10 text-primary-dark';
        label = 'Retour en transit';
        break;
      case 'delivered':
        colorClass = 'bg-primary-10 text-primary-dark';
        label = 'Retour livré';
        break;
      case 'completed':
      case 'refunded':
        colorClass = 'bg-success-10 text-success-dark';
        label = 'Retour terminé';
        break;
    }

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorClass} border border-transparent`}>
        {label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background-secondary pb-10">
      <SeoTags
        title={`${t('admin.dashboard.title')} | ${configuration?.name || 'CashLive'}`}
        description={t('admin.dashboard.subtitle')}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />
      {/* Breadcrumb Navigation */}
      <div className="bg-surface border-b border-border shadow-sm">
        <div className="pt-4 sm:pt-6 pb-4 px-4 sm:px-6">
          <nav className="flex items-center justify-end flex-wrap gap-2 sm:gap-3 text-sm">
            <Link
              to="/organizations/manage"
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-primary hover:text-primary-dark hover:bg-primary-20 transition-all duration-200 font-medium group"
            >
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <Home className="w-4 h-4" />
              <span>{t('admin.configuration.breadcrumb.organizations')}</span>
            </Link>
            <span className="text-foreground-secondary">/</span>
            <span className="text-foreground font-medium">
              {t('admin.sidebar.dashboard')}
            </span>
          </nav>
        </div>
      </div>

      {/* Titre et actions */}
      <Card variant="default" className="mt-6 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6">
        <CardBody className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                  {t('admin.dashboard.title')}
                </h1>
                <Badge variant="success" size="sm" className="border-0 flex-shrink-0">
                  <span className="relative flex h-2 w-2 mr-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                  </span>
                  Live
                </Badge>
              </div>
              <p className="mt-2 text-sm text-foreground-secondary font-medium">
                {t('admin.dashboard.subtitle')}
              </p>
            </div>
            {/* Bouton Dashboard Express - Tout à droite, responsive */}
            {configuration?.stripeConnectConfig?.accountId && isAdminForOrg && (
              <div className="flex-shrink-0 w-full sm:w-auto">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleOpenExpressDashboard}
                  disabled={isOpeningDashboard}
                  isLoading={isOpeningDashboard}
                  leftIcon={<ExternalLink className="w-4 h-4" />}
                  title={t('admin.dashboard.actions.openStripe')}
                  className="w-full sm:w-auto"
                >
                  {isOpeningDashboard ? t('admin.dashboard.stripe.opening') : t('admin.dashboard.actions.openStripe')}
                </Button>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Configuration merge : frais de livraison une fois pendant la période */}
      {hasShippingConfigured && (
        <div className="mt-3 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-primary-5 border border-primary-20">
            <div className="w-8 h-8 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
              <GitMerge className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground">
                {t('admin.configuration.shipping.orderMergeConfig.title')}
              </p>
              <p className="text-xs text-foreground-secondary mt-0.5">
                {getMergeConfigSummary()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6">
        {/* Statistics Cards - Affichées uniquement si canViewAnalytics */}
        {canViewStats && (() => {
          // Calculer le nombre total de cartes pour déterminer le nombre de colonnes
          const hasAlerts = stats.noTracking > 0 || (organizationBalance !== null && organizationBalance !== 0);
          const alertCount = (stats.noTracking > 0 ? 1 : 0) + (organizationBalance !== null && organizationBalance !== 0 ? 1 : 0);
          const totalCards = (stats.paid > 0 ? 5 : 4) + alertCount;

          return (
            <div className="space-y-4">
              <KPICardGrid columns={totalCards <= 5 ? 5 : totalCards <= 6 ? 6 : 7}>
                <KPICard
                  title={t('admin.dashboard.statistics.totalOrders')}
                  value={stats.total}
                  icon={<Package className="h-4 w-4" />}
                  color="primary"
                  onClick={() => setStatusFilter(['all'])}
                />

                <KPICard
                  title={t('admin.dashboard.statistics.inProgress')}
                  value={stats.pending}
                  icon={<Clock className="h-4 w-4" />}
                  color="warning"
                  onClick={() => setStatusFilter(['en_cours'])}
                />

                <KPICard
                  title={t('admin.dashboard.statistics.paidOrders')}
                  value={stats.paid}
                  icon={<CheckCircle className="h-4 w-4" />}
                  color="success"
                  onClick={() => setStatusFilter(['paid'])}
                />

                <KPICard
                  title={t('admin.dashboard.statistics.revenue')}
                  value={stats.totalAmount ?? 0}
                  icon={<CreditCard className="h-4 w-4" />}
                  color="success"
                  format="currency"
                />

                {/* Progress Bar de Préparation - Style détaillé "comme avant" */}
                {stats.paid > 0 && (
                  <div className="bg-surface border border-border rounded-xl p-3 hover:shadow-md transition-all duration-200 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-[11px] font-medium text-foreground-secondary mb-1">Préparation</h3>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-2xl font-bold text-success">{stats.preparation.ready}</span>
                          <span className="text-lg text-foreground-secondary">/</span>
                          <span className="text-2xl font-bold text-primary">{stats.paid}</span>
                        </div>
                      </div>
                      <div className="p-1.5 bg-primary-10 rounded-lg">
                        <PackageCheck className="h-5 w-5 text-primary" />
                      </div>
                    </div>

                    <div className="mt-3 space-y-2.5">
                      {/* Barre de progression */}
                      <div className="flex rounded-full h-1.5 bg-background-tertiary overflow-hidden">
                        <div className="bg-warning transition-all duration-500" style={{ width: `${(stats.preparation.toPrepare / stats.paid) * 100}%` }} />
                        <div className="bg-primary transition-all duration-500" style={{ width: `${(stats.preparation.inProgress / stats.paid) * 100}%` }} />
                        <div className="bg-success transition-all duration-500" style={{ width: `${(stats.preparation.ready / stats.paid) * 100}%` }} />
                      </div>

                      {/* Légende détaillée */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0" />
                          <span className="text-[10px] font-bold text-foreground">{stats.preparation.toPrepare}</span>
                          <span className="text-[8px] text-foreground-secondary uppercase tracking-tighter whitespace-nowrap">à faire</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span className="text-[10px] font-bold text-foreground">{stats.preparation.inProgress}</span>
                          <span className="text-[8px] text-foreground-secondary uppercase tracking-tighter whitespace-nowrap">en cours</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-success flex-shrink-0" />
                          <span className="text-[10px] font-bold text-foreground">{stats.preparation.ready}</span>
                          <span className="text-[8px] text-foreground-secondary uppercase tracking-tighter whitespace-nowrap">prêtes</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cartes d'alerte intégrées dans le même grid */}
                {stats.noTracking > 0 && (
                  <div className="cursor-pointer" onClick={() => setStatusFilter(['no_tracking'])}>
                    <KPICard
                      title="⚠️ Problème Parcels"
                      value={stats.noTracking}
                      subtitle="Sans tracking"
                      icon={<AlertCircle className="h-4 w-4 animate-pulse" />}
                      color="error"
                    />
                  </div>
                )}

                {organizationBalance !== null && organizationBalance !== 0 && (
                  <KPICard
                    title={organizationBalance < 0 ? t('admin.dashboard.debt.organizationDebt') : t('admin.dashboard.debt.organizationCredit')}
                    value={Math.abs(organizationBalance) / 100}
                    subtitle={organizationBalance < 0 ? t('admin.dashboard.debt.toPay') : t('admin.dashboard.debt.available')}
                    icon={<Wallet className="h-4 w-4" />}
                    color={organizationBalance < 0 ? 'error' : 'success'}
                    format="currency"
                  />
                )}
              </KPICardGrid>
            </div>
          );
        })()}

        {/* Filters and Search */}
        <Card variant="default" className="mt-6">
          <CardBody className="p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-foreground-secondary" />
                  <input
                    type="text"
                    placeholder={t('admin.dashboard.search.placeholder')}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary text-foreground bg-surface"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 sm:w-auto sm:min-w-[250px] relative">
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className="w-full flex items-center justify-between px-3 py-2 border border-border rounded-lg bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary transition-all"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <MaterialIcon icon="filter_list" style={{ fontSize: '18px' }} className="text-foreground-secondary flex-shrink-0" />
                      <div className="flex gap-1 overflow-x-auto no-scrollbar">
                        {statusFilter.includes('all') ? (
                          <span className="text-sm">{t('admin.dashboard.table.allStatuses')}</span>
                        ) : (
                          statusFilter.map(val => (
                            <Badge key={val} variant="info" size="sm" className="whitespace-nowrap">
                              {statusOptions.find(opt => opt.value === val)?.label || val}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-foreground-secondary transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showStatusDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowStatusDropdown(false)}
                      />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-border rounded-xl shadow-xl z-20 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="p-2 max-h-[300px] overflow-y-auto">
                          {statusOptions.map((option) => {
                            const isSelected = statusFilter.includes(option.value);
                            return (
                              <button
                                key={option.value}
                                onClick={() => toggleStatus(option.value)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors mb-0.5 last:mb-0 ${isSelected
                                  ? 'bg-primary-10 text-primary font-semibold'
                                  : 'text-foreground hover:bg-background-tertiary shadow-sm'
                                  }`}
                              >
                                <span className="flex items-center gap-3">
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected
                                    ? 'bg-primary border-primary'
                                    : 'border-border bg-background'
                                    }`}>
                                    {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                  </div>
                                  {option.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* Range Date Picker avec react-datepicker - Responsive */}
                <div className="flex-1 sm:flex-initial sm:min-w-[280px]">
                  <ReactDatePicker
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateRangeChange}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Sélectionner une période"
                    customInput={<CustomDatePickerInput />}
                    isClearable={true}
                    monthsShown={1}
                    calendarStartDay={1}
                    withPortal={typeof window !== 'undefined' && window.innerWidth < 640}
                    popperClassName="react-datepicker-popper-responsive"
                    popperPlacement="bottom-start"
                  >
                    <PeriodButtons />
                  </ReactDatePicker>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Orders Table */}
        <Card variant="default" className="overflow-hidden mt-6">
          {/* Table Header with Action Buttons */}
          <div className="px-3 sm:px-6 py-3 border-b border-border-light bg-background-secondary flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            {/* Left side - Title and Batch Parcel Button */}
            <div className="flex items-center gap-3">
              {/* Batch Parcel Generation Button - FAR LEFT */}
              {eligibleOrdersCount > 0 && !isBatchSelectionMode && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={enterBatchSelectionMode}
                  leftIcon={<CreditCard className="h-3 w-3" />}
                  title={`Générer les colis pour ${eligibleOrdersCount} commande(s)`}
                >
                  Générer les colis manquants ({eligibleOrdersCount})
                </Button>
              )}

              {/* Exit Batch Mode Button */}
              {isBatchSelectionMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exitBatchSelectionMode}
                  leftIcon={<X className="h-3 w-3" />}
                >
                  Annuler la sélection
                </Button>
              )}

              {/* Title - shown when not in batch mode or no eligible orders */}
              {eligibleOrdersCount === 0 && !isBatchSelectionMode && (
                <>
                  <h3 className="text-base sm:text-lg font-semibold text-foreground">{t('admin.dashboard.table.orders')}</h3>
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  )}
                </>
              )}

              {/* Loading indicator when batch button is shown */}
              {(eligibleOrdersCount > 0 || isBatchSelectionMode) && loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              )}
            </div>

            {/* Right side - Other action buttons */}
            <div className="flex flex-wrap gap-2">
              {/* Export CSV Button */}
              {filteredOrders.length > 0 && canManageOrdersForOrg && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleExportCsv}
                  leftIcon={<FileDown className="h-3 w-3" />}
                  title={t('admin.dashboard.actions.exportFiltered')}
                >
                  {t('admin.dashboard.actions.export')} ({filteredOrders.length})
                </Button>
              )}

              {/* Bulk Label Actions */}
              {(() => {
                const ordersWithLabels = filteredOrders.filter(order =>
                  isOrderPaid(order) && getOrderLabelUrl(order)
                );
                return ordersWithLabels.length > 0 && canManageOrdersForOrg && (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={printAllLabels}
                      leftIcon={<Truck className="h-3 w-3" />}
                      title={`Imprimer toutes les étiquettes (${ordersWithLabels.length})`}
                    >
                      Imprimer Toutes ({ordersWithLabels.length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadAllLabels}
                      leftIcon={<Download className="h-3 w-3" />}
                      title={`Télécharger toutes les étiquettes (${ordersWithLabels.length})`}
                    >
                      Télécharger Toutes ({ordersWithLabels.length})
                    </Button>
                  </>
                );
              })()}

              {/* Expand/Collapse Buttons */}
              <Button
                variant="ghost"
                size="sm"
                onClick={expandAllOrders}
                leftIcon={<ChevronDown className="h-3 w-3" />}
                title="Tout développer"
              >
                {t('admin.dashboard.table.expandAll')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={collapseAllOrders}
                leftIcon={<ChevronRight className="h-3 w-3" />}
                title="Tout réduire"
              >
                {t('admin.dashboard.table.collapseAll')}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-border-light table-auto">
              <thead className="bg-background-tertiary">
                <tr>
                  {/* Checkbox column for batch parcel generation (only visible in batch selection mode) */}
                  {isBatchSelectionMode && (
                    <th className="w-14 px-3 py-3">
                      <div className="flex items-center justify-center">
                        <label className="relative flex items-center cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.size > 0 && selectedOrderIds.size === eligibleOrdersCount}
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllEligibleOrders();
                              } else {
                                deselectAllOrders();
                              }
                            }}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center group-hover:border-primary ${selectedOrderIds.size > 0 && selectedOrderIds.size === eligibleOrdersCount
                            ? 'bg-primary border-primary shadow-sm'
                            : 'border-border-medium bg-surface'
                            }`}>
                            {selectedOrderIds.size > 0 && selectedOrderIds.size === eligibleOrdersCount && (
                              <CheckCircle className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                        </label>
                      </div>
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[180px]">
                    {t('admin.dashboard.table.customer').toUpperCase()}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[100px]">
                    <div className="flex items-center gap-1.5">
                      <span>{t('admin.dashboard.table.totalAmount')}</span>
                      <Tooltip
                        title={
                          <div className="text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-success font-semibold">Vert</span>: Montant des produits
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-error font-semibold">Rouge</span>: Frais de livraison
                            </div>
                          </div>
                        }
                        placement="top"
                        arrow
                        disableTouchListener
                      >
                        <Info className="h-3.5 w-3.5 text-foreground-secondary/60 cursor-help hover:text-foreground-secondary transition-colors" />
                      </Tooltip>
                    </div>
                  </th>
                  {hasAnyDebt && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[80px]">
                      {t('admin.dashboard.debt.columnTitle').toUpperCase()}
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[120px]">
                    {t('admin.dashboard.table.status').toUpperCase()}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[120px]">
                    {t('admin.dashboard.table.preparation')}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[120px]">
                    {t('admin.dashboard.table.date').toUpperCase()}
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider min-w-[80px]">
                    {t('admin.dashboard.table.actions').toUpperCase()}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border-light">
                {paginatedOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    {/* Main Order Row */}
                    <tr className={`hover:bg-background-secondary transition-colors ${order.id && selectedOrderIds.has(order.id) ? 'bg-primary-5 border-l-2 border-l-primary' : ''
                      }`}>
                      {/* Checkbox for batch parcel generation (only visible in batch selection mode) */}
                      {isBatchSelectionMode && (
                        <td className="w-14 px-3 py-4">
                          {orderNeedsParcelGeneration(order) ? (
                            <div className="flex items-center justify-center">
                              <label className="relative flex items-center cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={order.id ? selectedOrderIds.has(order.id) : false}
                                  onChange={() => order.id && toggleOrderSelection(order.id)}
                                  className="peer sr-only"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center group-hover:border-primary ${order.id && selectedOrderIds.has(order.id)
                                  ? 'bg-primary border-primary shadow-sm'
                                  : 'border-border-medium bg-surface hover:bg-background-tertiary'
                                  }`}>
                                  {order.id && selectedOrderIds.has(order.id) && (
                                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                                  )}
                                </div>
                              </label>
                            </div>
                          ) : (
                            <div className="w-5 h-5" /> // Placeholder to maintain alignment
                          )}
                        </td>
                      )}
                      <td className="px-3 sm:px-6 py-4 min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleOrderExpansion(order.id!)}
                            className="p-1 hover:bg-background-tertiary rounded-lg flex-shrink-0 transition-colors"
                          >
                            {expandedOrders.has(order.id!) ? (
                              <ChevronDown className="h-4 w-4 text-foreground-secondary" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-foreground-secondary" />
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            {order.products?.[0]?.tiktokUsername && (
                              <div className="text-xs sm:text-sm text-primary truncate">@{order.products[0].tiktokUsername}</div>
                            )}
                            <div className="text-xs sm:text-sm font-semibold text-foreground truncate">
                              {order.orderInfo.firstName} {order.orderInfo.lastName}
                            </div>
                            <div className="text-xs sm:text-sm text-foreground-secondary truncate">
                              {order.orderInfo.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 min-w-[100px]">
                        <div className="text-sm font-semibold">
                          {(() => {
                            const paidProductsTotal = order.products?.reduce((sum: number, p: Product) =>
                              p.paymentInfo.status === 'paid' ? sum + Number.parseFloat(p.amount || '0') : sum, 0) || 0;
                            const deliveryCost = order.deliveryInfo.shippingCost || 0;

                            return (
                              <span>
                                <span className="text-success">€{paidProductsTotal.toFixed(2)}</span>
                                {deliveryCost > 0 && (
                                  <>
                                    <span className="text-foreground-secondary"> + </span>
                                    <span className="text-error">€{deliveryCost.toFixed(2)}</span>
                                  </>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      {hasAnyDebt && (
                        <td className="px-3 sm:px-6 py-4 min-w-[80px]">
                          {/* Colonne Dette - Somme des deltas de debtHistory */}
                          {(() => {
                            const debtHistory = (order as any).debtHistory;
                            if (!debtHistory || !Array.isArray(debtHistory) || debtHistory.length === 0) {
                              return <span className="text-sm text-foreground-secondary">—</span>;
                            }
                            // Somme de tous les deltas (positif = dette, négatif = crédit)
                            const totalDebt = debtHistory.reduce((sum: number, entry: any) => sum + (entry.delta || 0), 0);
                            const isDebt = totalDebt > 0;
                            const isCredit = totalDebt < 0;

                            return (
                              <button
                                onClick={() => {
                                  setSelectedDebtOrder(order);
                                  setShowDebtModal(true);
                                }}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${isDebt
                                  ? 'bg-error-10 text-error-dark border border-error-20 hover:bg-error-20'
                                  : isCredit
                                    ? 'bg-success-10 text-success-dark border border-success-20 hover:bg-success-20'
                                    : 'bg-background-tertiary text-foreground-secondary border border-border hover:bg-border'
                                  }`}
                              >
                                {isDebt ? '-' : '+'}{(Math.abs(totalDebt) / 100).toFixed(2)}€
                                <Info className="h-3 w-3" />
                              </button>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                        <div className="flex flex-col gap-1">
                          {/* Ligne 1: statut de livraison (payée, remboursée ou à régler) — au-dessus de la période */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {(() => {
                              const deliveryPaymentStatus = order.deliveryInfo?.paymentStatus as string | undefined;
                              return (deliveryPaymentStatus === 'paid' || deliveryPaymentStatus === 'refunded') && (
                                <StatusBadge
                                  status={isShippingRefunded(order) ? 'refunded' : 'paid'}
                                  size="sm"
                                  showIcon={false}
                                >
                                  {isShippingRefunded(order)
                                    ? t('admin.dashboard.shipping.refunded')
                                    : t('admin.dashboard.shipping.paid')}
                                </StatusBadge>
                              );
                            })()}
                            {/* Frais de livraison à régler : livraison avec coût mais pas encore payée ni remboursée */}
                            {(() => {
                              const deliveryPaymentStatus = order.deliveryInfo?.paymentStatus as string | undefined;
                              return order.deliveryInfo?.shippingCost != null && Number(order.deliveryInfo.shippingCost) > 0 && deliveryPaymentStatus !== 'paid' && deliveryPaymentStatus !== 'refunded' && (
                                <StatusBadge status="pending" size="sm" showIcon={false}>
                                  {t('admin.dashboard.shipping.unpaid', 'Frais de livraison à régler')}
                                </StatusBadge>
                              );
                            })()}
                          </div>
                          {/* Ligne 2: Période ouverte (label raccourci) + icônes colis */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Période ouverte : affichée uniquement si la livraison est payée. Label court : "Période ouverte · 25/02 23:59". */}
                            {(() => {
                              if (isShippingRefunded(order)) return null;
                              if ((order.deliveryInfo?.paymentStatus as string | undefined) !== 'paid') return null;
                              const snap = (order as FirebaseOrder & { mergeConfigSnapshot?: { periodStart?: string; periodEnd?: string; wasCreatedInOpenDays?: boolean } | null }).mergeConfigSnapshot;
                              if (!snap?.periodStart || !snap?.periodEnd) return null;
                              const start = new Date(snap.periodStart);
                              const end = new Date(snap.periodEnd);
                              const now = new Date();
                              if (now < start || now > end) return null;
                              const formatEndDateLong = (d: Date) => {
                                const day = d.getDate().toString().padStart(2, '0');
                                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                const year = d.getFullYear();
                                const h = d.getHours().toString().padStart(2, '0');
                                const m = d.getMinutes().toString().padStart(2, '0');
                                return `${day}/${month}/${year} ${h}:${m}`;
                              };
                              const formatEndDateShort = (d: Date) => {
                                const day = d.getDate().toString().padStart(2, '0');
                                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                const h = d.getHours().toString().padStart(2, '0');
                                const m = d.getMinutes().toString().padStart(2, '0');
                                return `${day}/${month} ${h}:${m}`;
                              };
                              const endDateLong = formatEndDateLong(end);
                              const endDateShort = formatEndDateShort(end);
                              const tooltipBase = t('admin.dashboard.orderMerge.periodOpenTooltip', 'Le client ne repaie pas les frais de livraison pour une commande passée dans cette période (même jour ou plage de jours ouverts).');
                              const tooltipUntil = t('admin.dashboard.orderMerge.periodOpenTooltipUntil', 'Valable jusqu\'au {{date}}.', { date: endDateLong });
                              const tooltipTitle = `${tooltipBase} ${tooltipUntil}`;
                              const shortLabel = t('admin.dashboard.orderMerge.periodOpenShortUntilShort', 'Période ouverte · {{date}}', { date: endDateShort });
                              return (
                                <Tooltip title={tooltipTitle} placement="top" arrow enterDelay={300}>
                                  <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-success-10 text-success-dark border border-success-20 whitespace-nowrap cursor-help">
                                    {shortLabel}
                                  </span>
                                </Tooltip>
                              );
                            })()}
                            {/* Icône d'attention si commande payée mais pas de colis (parcelId = source de vérité) */}
                            {isOrderPaid(order) && !hasTrackingOrLabel(order) && (() => {
                              const parcelError = order.deliveryInfo?.parcelCreationError;
                              const isInsufficientCredits = isInsufficientCreditsParcelError(order);
                              const isParcelToGenerate = isManualGenerationPending(order);
                              const isRealParcelError = hasRealParcelError(order);

                              if (isRealParcelError) {
                                return (
                                  <Tooltip
                                    title={`❌ ${parcelError?.message || 'Erreur de génération du colis'}`}
                                    placement="top"
                                    arrow
                                    disableTouchListener
                                  >
                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-error-10 border border-error-20 rounded-full cursor-help">
                                      <AlertCircle className="h-4 w-4 text-error animate-pulse" />
                                      <span className="text-[10px] font-bold text-error-dark">Erreur</span>
                                    </div>
                                  </Tooltip>
                                );
                              }

                              // Colis à générer manuellement (mode API Key/PayPal OU crédits insuffisants)
                              if (isParcelToGenerate || isInsufficientCredits) {
                                return (
                                  <Tooltip
                                    title={isBatchSelectionMode
                                      ? "Cochez pour inclure dans la génération de colis"
                                      : "Cliquez sur 'Générer les colis' pour créer l'étiquette"
                                    }
                                    placement="top"
                                    arrow
                                    disableTouchListener
                                  >
                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-warning-10 border border-warning-20 rounded-full cursor-help whitespace-nowrap">
                                      <Truck className="h-4 w-4 text-warning-dark flex-shrink-0" />
                                      <span className="text-[10px] font-bold text-warning-dark whitespace-nowrap">À générer</span>
                                    </div>
                                  </Tooltip>
                                );
                              }

                              // Mode Stripe Connect sans erreur explicite: Erreur automatique (colis aurait dû être créé)
                              return (
                                <Tooltip
                                  title="⚠️ Commande payée mais aucune information de livraison. Vérifiez la création du colis SendCloud."
                                  placement="top"
                                  arrow
                                  disableTouchListener
                                >
                                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-error-10 border border-error-20 rounded-full cursor-help">
                                    <AlertCircle className="h-4 w-4 text-error animate-pulse" />
                                    <span className="text-[10px] font-bold text-error-dark">Tracking manquant</span>
                                  </div>
                                </Tooltip>
                              );
                            })()}
                          </div>
                          {order.deliveryInfo.shippingStatusMessage && (
                            <div className="flex items-center gap-2">
                              <Badge variant="info" size="sm">
                                {order.deliveryInfo.shippingStatusMessage}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                        {order.deliveryInfo.paymentStatus === 'paid' && (
                          <CompactStepper>
                            {/* Étape 1: À faire */}
                            <Tooltip
                              title={order.preparationStatus === undefined ? "À faire" : "Revenir à: À faire"}
                              placement="top"
                              arrow
                              disableTouchListener
                            >
                              <MiniStepIcon
                                ownerState={{
                                  active: order.preparationStatus === 'todo' || !order.preparationStatus,
                                  completed: order.preparationStatus === 'in_progress' || order.preparationStatus === 'ready',
                                  iconType: 'clock'
                                }}
                                onClick={canManageOrdersForOrg ? () => updateOrderPreparationStatus(order.id!, 'todo') : undefined}
                                style={canManageOrdersForOrg ? {} : { cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <Clock size={12} />
                              </MiniStepIcon>
                            </Tooltip>

                            {/* Ligne de connexion 1 */}
                            <div className={`step-connector ${order.preparationStatus === 'in_progress' || order.preparationStatus === 'ready' ? 'active' : ''}`} />

                            {/* Étape 2: En préparation */}
                            <Tooltip
                              title={
                                order.preparationStatus === 'in_progress'
                                  ? "En préparation"
                                  : order.preparationStatus === 'ready'
                                    ? "Revenir à: En préparation"
                                    : "Commencer la préparation"
                              }
                              placement="top"
                              arrow
                              disableTouchListener
                            >
                              <MiniStepIcon
                                ownerState={{
                                  active: order.preparationStatus === 'in_progress',
                                  completed: order.preparationStatus === 'ready',
                                  iconType: 'chef'
                                }}
                                onClick={canManageOrdersForOrg ? () => updateOrderPreparationStatus(order.id!, 'in_progress') : undefined}
                                style={canManageOrdersForOrg ? {} : { cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <MaterialIcon
                                  icon="inventory_2"
                                  style={{ fontSize: '14px' }}
                                />
                              </MiniStepIcon>
                            </Tooltip>

                            {/* Ligne de connexion 2 */}
                            <div className={`step-connector ${order.preparationStatus === 'ready' ? 'active' : ''}`} />

                            {/* Étape 3: Prêt */}
                            <Tooltip
                              title={
                                order.preparationStatus === 'ready'
                                  ? "Prêt"
                                  : "Marquer comme prêt"
                              }
                              placement="top"
                              arrow
                              disableTouchListener
                            >
                              <MiniStepIcon
                                ownerState={{
                                  active: order.preparationStatus === 'ready',
                                  completed: false,
                                  iconType: 'check'
                                }}
                                onClick={canManageOrdersForOrg ? () => updateOrderPreparationStatus(order.id!, 'ready') : undefined}
                                style={canManageOrdersForOrg ? {} : { cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <CheckCircle size={12} />
                              </MiniStepIcon>
                            </Tooltip>
                          </CompactStepper>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-sm text-foreground-secondary min-w-[120px]">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-2 sm:px-6 py-4 text-sm font-medium min-w-[100px]">
                        <div className="flex items-center gap-1.5">
                          {/* Bouton Voir détails */}
                          <Tooltip title="Voir détails" placement="top" arrow disableTouchListener>
                            <button
                              onClick={() => openOrderModal(order)}
                              className="p-1.5 rounded-lg text-primary hover:bg-primary-10 hover:text-primary-dark transition-all duration-200"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                          </Tooltip>

                          {/* Quick status updates for first product */}
                          {canManageOrdersForOrg && order.products && order.products.length > 0 && order.products[0].paymentInfo.status === 'pending' && (
                            <Tooltip title="Marquer le premier produit comme payé" placement="top" arrow disableTouchListener>
                              <button
                                onClick={() => showPaymentConfirmation(order.id!, 0, 'paid', order.products[0].reference)}
                                className="p-1.5 rounded-lg text-success hover:bg-success-10 hover:text-success-dark transition-all duration-200"
                              >
                                <CheckCircle className="h-5 w-5" />
                              </button>
                            </Tooltip>
                          )}

                          {/* Print label for orders with paid status */}
                          {canManageOrdersForOrg && isOrderPaid(order) && getOrderLabelUrl(order) && (
                            <>
                              <Tooltip title="Imprimer l'étiquette" placement="top" arrow disableTouchListener>
                                <button
                                  onClick={() => printLabel(order)}
                                  className="p-1.5 rounded-lg text-primary hover:bg-primary-10 hover:text-primary-dark transition-all duration-200"
                                >
                                  <Truck className="h-5 w-5" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Télécharger l'étiquette" placement="top" arrow disableTouchListener>
                                <button
                                  onClick={() => downloadLabel(order)}
                                  className="p-1.5 rounded-lg text-success hover:bg-success-10 hover:text-success-dark transition-all duration-200"
                                >
                                  <Download className="h-5 w-5" />
                                </button>
                              </Tooltip>
                            </>
                          )}

                        </div>
                      </td>
                    </tr>

                    {/* Expanded Product Rows */}
                    {expandedOrders.has(order.id!) && Boolean(order.products?.length) && (
                      order.products.map((product: Product, productIndex: number) => (
                        <tr key={`${order.id}-product-${productIndex}`} className="bg-background-secondary">
                          {/* Checkbox column for batch parcel generation (only visible in batch selection mode) */}
                          {isBatchSelectionMode && (
                            <td className="w-14 px-3 py-4">
                              {/* Empty cell for product rows - checkbox is at order level */}
                            </td>
                          )}
                          <td className="px-3 sm:px-6 py-4 min-w-[180px]">
                            <div className="text-sm text-foreground pl-10">
                              <span className="font-medium">{product.reference}</span>
                              {product.model && (
                                <div className="text-foreground-secondary mt-0.5 flex flex-col gap-0.5">
                                  {product.model.split(/\s*\+\s*/).map((part, i) => {
                                    const t = part.trim();
                                    return t ? <div key={i}>{t}</div> : null;
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 min-w-[100px]">
                            <div className="text-sm font-semibold text-foreground">
                              €{Number.parseFloat(product.amount || '0').toFixed(2)}
                            </div>
                          </td>
                          {hasAnyDebt && (
                            <td className="px-3 sm:px-6 py-4 min-w-[80px]">
                              {/* Empty cell for product rows - debt is at order level */}
                            </td>
                          )}
                          <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                            <div className="pl-10 flex flex-col items-start gap-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {product.paymentInfo.status === 'failed' && getLatestFailureCode(product) ? (
                                  <Tooltip title={getLatestFailureCode(product)} placement="top" arrow disableTouchListener>
                                    <span className="inline-flex">
                                      <StatusBadge
                                        status={getProductDisplayStatus(order, product, productIndex) as any}
                                        size="sm"
                                        showIcon={false}
                                      />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <StatusBadge
                                    status={getProductDisplayStatus(order, product, productIndex) as any}
                                    size="sm"
                                    showIcon={false}
                                  />
                                )}
                                {product.paymentInfo.method && (
                                  <Tooltip title={`Payé via ${getPaymentProviderInfo(product.paymentInfo.method, product).name}`} arrow>
                                    <span>
                                      <Badge
                                        variant={getPaymentProviderInfo(product.paymentInfo.method, product).badgeVariant}
                                        size="sm"
                                        className="gap-1"
                                      >
                                        {(() => {
                                          const provider = getPaymentProviderInfo(product.paymentInfo.method, product);
                                          const Icon = provider.icon;
                                          return <Icon className="w-3 h-3 flex-shrink-0" />;
                                        })()}
                                        {getPaymentProviderInfo(product.paymentInfo.method, product).name}
                                      </Badge>
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                            {/* Empty cell for product rows preparation status */}
                          </td>
                          <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                            <div className="text-sm text-foreground-secondary pl-10">
                              {product.createdAt ? formatDate(product.createdAt) : 'N/A'}
                            </div>
                          </td>
                          <td className="px-2 sm:px-6 py-4 min-w-[80px]">
                            {/* Empty cell for product rows - actions are at order level */}
                          </td>
                        </tr>
                      ))
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>


          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-foreground-secondary mx-auto mb-4" />
              <p className="text-foreground-secondary">
                {orders.length === 0 ? t('admin.dashboard.table.noOrders') : t('admin.dashboard.table.noOrdersDesc')}
              </p>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-background-secondary to-surface px-6 py-4 flex items-center justify-between border-t border-border">
              {/* Mobile */}
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >
                  Suivant
                </Button>
              </div>

              {/* Desktop */}
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <p className="text-sm text-foreground-secondary">
                    <span className="font-semibold text-foreground">{startIndex + 1}</span> à{' '}
                    <span className="font-semibold text-foreground">{Math.min(endIndex, filteredOrders.length)}</span> sur{' '}
                    <span className="font-semibold text-foreground">{filteredOrders.length}</span>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    leftIcon={<ChevronLeft className="w-5 h-5" />}
                    title={t('admin.dashboard.pagination.previous')}
                    className="w-10 h-10 p-0"
                  />

                  <div className="flex items-center gap-1 px-4 py-2 bg-surface rounded-xl border border-border shadow-sm">
                    <span className="text-sm font-semibold text-foreground">{currentPage}</span>
                    <span className="text-sm text-foreground-secondary">/</span>
                    <span className="text-sm text-foreground-secondary">{totalPages}</span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    rightIcon={<ChevronRight className="w-5 h-5" />}
                    title={t('admin.dashboard.pagination.next')}
                    className="w-10 h-10 p-0"
                  />
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Debt Details Modal */}
      <TailwindDialog
        isOpen={showDebtModal && !!selectedDebtOrder}
        onClose={() => {
          setShowDebtModal(false);
          setSelectedDebtOrder(null);
        }}
        title="Détails de la dette"
        maxWidth="lg"
        variant="themed"
      >
        {selectedDebtOrder && (
          <DialogContent className="p-6">
            {(() => {
              const debtHistory = (selectedDebtOrder as any).debtHistory as any[] | undefined;
              if (!debtHistory || debtHistory.length === 0) {
                return <p className="text-foreground-secondary">Aucun mouvement de dette pour cette commande.</p>;
              }

              const totalDebt = debtHistory.reduce((sum, entry) => sum + (entry.delta || 0), 0);
              const isDebt = totalDebt > 0;

              // Helper function pour formater les raisons avec descriptions user-friendly
              const formatReason = (reason: string, delta?: number, metadata?: { shippingCustomPrice?: string; shippingActualPrice?: string; shippingDifference?: string }) => {
                // Crédit "dette collectée" (pas de différence livraison sur cette commande) → libellé dédié
                const isShippingCreditNoDiff = reason === 'shipping_balance_delta_payment' && (delta ?? 0) < 0 && metadata
                  && (metadata.shippingDifference === '0' || (metadata.shippingCustomPrice !== undefined && metadata.shippingActualPrice !== undefined && Number(metadata.shippingCustomPrice) === Number(metadata.shippingActualPrice)));
                if (isShippingCreditNoDiff) return 'Crédit (dette collectée sur le paiement)';

                const reasonLabels: Record<string, string> = {
                  // Dettes liées à la livraison
                  'shipping_balance_delta_payment': 'Différence de frais de livraison',
                  'shipping_debt_added_missing_carrier_refund': 'Frais de livraison non remboursés par le transporteur',
                  'shipping_debt_cancelled': 'Annulation des frais de livraison',
                  'shipping_delta_cancelled_refund': 'Annulation des frais de livraison suite au remboursement',

                  // Dettes liées aux commissions
                  'commission_debt_refund': 'Commission plateforme suite au remboursement',
                  'commission_debt_payment': 'Commission plateforme',

                  // Dettes générales
                  'refund_adjustment': 'Ajustement suite à un remboursement',
                  'manual_adjustment': 'Ajustement manuel',
                  'correction': 'Correction de solde',
                  'unspecified': 'Mouvement de compte',
                };
                return reasonLabels[reason] || 'Mouvement de compte';
              };

              // Helper function pour formater les explications user-friendly
              const getReasonExplanation = (reason: string, delta: number, metadata?: { shippingCustomPrice?: string; shippingActualPrice?: string; shippingDifference?: string }) => {
                const isDebt = delta > 0;
                const amount = (Math.abs(delta) / 100).toFixed(2);

                // Pour shipping_balance_delta_payment en crédit : si pas de différence livraison, le crédit vient de la collecte de dette existante
                const isShippingCreditNoDiff = reason === 'shipping_balance_delta_payment' && !isDebt && metadata
                  && (metadata.shippingDifference === '0' || (metadata.shippingCustomPrice !== undefined && metadata.shippingActualPrice !== undefined && Number(metadata.shippingCustomPrice) === Number(metadata.shippingActualPrice)));

                const explanations: Record<string, string> = {
                  'shipping_balance_delta_payment': isShippingCreditNoDiff
                    ? `Crédit appliqué lors du paiement (dette existante collectée sur ce paiement : ${amount}€).`
                    : isDebt
                      ? `Différence de frais de livraison. Un delta de ${amount}€ a été appliqué lors du paiement entre ce que le client a payé et le coût réel transporteur.`
                      : `Les frais de livraison réels étaient moins élevés que ce qui a été facturé au client. La différence de ${amount}€ vous est créditée.`,
                  'delivery_cost_debt_refund': `Coût réel livraison. Ce mouvement représente le coût réel de la livraison de ${amount}€ facturé par le transporteur.`,
                  'delivery_debt_cancelled_sendcloud_refund': `Annulation dette livraison. Annulation de la dette de livraison de ${amount}€ remboursée par Sendcloud.`,
                  'shipping_debt_added_missing_carrier_refund': `Le transporteur n'a pas pu rembourser l'étiquette d'expédition. Les frais de ${amount}€ restent à votre charge.`,
                  'shipping_debt_cancelled': `Les frais de livraison précédemment comptabilisés ont été annulés.`,
                  'shipping_delta_cancelled_refund': `Annulation du delta de frais de livraison suite au remboursement de la commande.`,
                  'commission_debt_refund': `Lors du remboursement, la commission plateforme (${amount}€) n'a pas pu être récupérée et reste à votre charge.`,
                  'commission_debt_payment': `Commission de la plateforme prélevée sur cette transaction.`,
                  'refund_adjustment': `Ajustement de votre solde suite au remboursement de la commande.`,
                  'manual_adjustment': `Ajustement effectué manuellement sur votre compte.`,
                  'correction': `Correction apportée à votre solde.`,
                };
                return explanations[reason] || null;
              };

              // Helper function pour formater la date
              const formatDebtDate = (createdAt: any) => {
                if (!createdAt) return 'Date inconnue';
                let date: Date;
                if (createdAt.toDate) {
                  date = createdAt.toDate();
                } else if (createdAt.seconds) {
                  date = new Date(createdAt.seconds * 1000);
                } else if (typeof createdAt === 'string') {
                  date = new Date(createdAt);
                } else {
                  return 'Date inconnue';
                }
                return new Intl.DateTimeFormat('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }).format(date);
              };

              return (
                <div className="space-y-6">
                  {/* Résumé */}
                  <div className={`p-4 rounded-lg ${isDebt ? 'bg-error-10 border border-error-20' : 'bg-success-10 border border-success-20'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {isDebt ? t('admin.dashboard.debt.totalDebtForOrder') : t('admin.dashboard.debt.totalCreditForOrder')}
                        </h4>
                        <p className="text-sm text-foreground-secondary mt-1">
                          {t('admin.dashboard.debt.orderNumber', { number: selectedDebtOrder.id?.slice(-8) })}
                        </p>
                      </div>
                      <span className={`text-2xl font-bold ${isDebt ? 'text-error' : 'text-success'}`}>
                        {isDebt ? '-' : '+'}{(Math.abs(totalDebt) / 100).toFixed(2)} €
                      </span>
                    </div>
                  </div>

                  {/* Explication */}
                  <div className="bg-primary-10 border border-primary-20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-primary-dark">
                        <p className="font-semibold mb-1">Comprendre votre solde</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li><span className="text-error font-medium">Solde négatif (dette)</span> : montant qui sera déduit de vos prochains reversements</li>
                          <li><span className="text-success font-medium">Solde positif (crédit)</span> : montant qui sera ajouté à vos prochains reversements</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Historique des mouvements */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Historique des mouvements</h4>
                    <div className="space-y-3">
                      {debtHistory.map((entry, idx) => (
                        <div
                          key={entry.id || idx}
                          className={`p-4 rounded-lg border ${entry.delta > 0
                            ? 'bg-error-10 border-error-20'
                            : 'bg-success-10 border-success-20'
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-lg font-bold ${entry.delta > 0 ? 'text-error' : 'text-success'}`}>
                                  {entry.delta > 0 ? '-' : '+'}{(Math.abs(entry.delta) / 100).toFixed(2)} €
                                </span>
                                <span className="text-xs text-foreground-secondary">
                                  {formatDebtDate(entry.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-foreground">
                                {formatReason(entry.reason, entry.delta, entry.metadata)}
                              </p>
                              {/* Afficher une explication user-friendly au lieu de la note technique */}
                              {getReasonExplanation(entry.reason, entry.delta, entry.metadata) && (
                                <p className="text-xs text-foreground-secondary mt-1">
                                  {getReasonExplanation(entry.reason, entry.delta, entry.metadata)}
                                </p>
                              )}

                              {/* Détail du calcul : différence entre prix choisi par l'utilisateur et tarif plateforme (coût réel) */}
                              {entry.metadata && (entry.metadata.shippingCustomPrice !== undefined || entry.metadata.shippingActualPrice !== undefined) && (
                                <div className="mt-2 text-xs bg-surface/50 rounded-lg p-3 border border-border">
                                  <div className="font-semibold text-foreground mb-2">Détail du calcul</div>
                                  <div className="space-y-1">
                                    {entry.metadata.shippingCustomPrice !== undefined && (
                                      <div className="flex justify-between">
                                        <span className="text-foreground-secondary">Prix choisi par l'utilisateur :</span>
                                        <span className="text-foreground font-medium">{(Number(entry.metadata.shippingCustomPrice) / 100).toFixed(2)} €</span>
                                      </div>
                                    )}
                                    {entry.metadata.shippingActualPrice !== undefined && (
                                      <div className="flex justify-between">
                                        <span className="text-foreground-secondary">Tarif plateforme (coût réel) :</span>
                                        <span className="text-foreground font-medium">{(Number(entry.metadata.shippingActualPrice) / 100).toFixed(2)} €</span>
                                      </div>
                                    )}
                                    {entry.metadata.shippingCustomPrice !== undefined && entry.metadata.shippingActualPrice !== undefined && (
                                      <div className="flex justify-between pt-1 mt-1 border-t border-border">
                                        <span className="text-foreground font-semibold">Différence (prix choisi − tarif plateforme) :</span>
                                        <span className={`font-bold ${(Number(entry.metadata.shippingCustomPrice) - Number(entry.metadata.shippingActualPrice)) >= 0 ? 'text-success' : 'text-error'}`}>
                                          {((Number(entry.metadata.shippingCustomPrice) - Number(entry.metadata.shippingActualPrice)) / 100).toFixed(2)} €
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Solde avant/après - Affichage clair */}
                          <div className="mt-3 pt-2 border-t border-border">
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-foreground-secondary">Solde avant :</span>
                                <span className={`font-medium ${entry.balanceBefore < 0 ? 'text-error' : entry.balanceBefore > 0 ? 'text-success' : 'text-foreground'}`}>
                                  {entry.balanceBefore < 0 ? '' : entry.balanceBefore > 0 ? '+' : ''}{(entry.balanceBefore / 100).toFixed(2)} €
                                </span>
                              </div>
                              <span className="text-foreground-secondary mx-2">→</span>
                              <div className="flex items-center gap-2">
                                <span className="text-foreground-secondary">Solde après :</span>
                                <span className={`font-semibold ${entry.balanceAfter < 0 ? 'text-error' : entry.balanceAfter > 0 ? 'text-success' : 'text-foreground'}`}>
                                  {entry.balanceAfter < 0 ? '' : entry.balanceAfter > 0 ? '+' : ''}{(entry.balanceAfter / 100).toFixed(2)} €
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        )}
      </TailwindDialog>

      {/* Order Details Modal */}
      <TailwindDialog
        isOpen={showOrderModal && !!selectedOrder}
        onClose={() => setShowOrderModal(false)}
        title={`Commande #${selectedOrder?.id?.slice(-8)}`}
        maxWidth="4xl"
        showCloseButton={false}
        variant="themed"
      >
        {selectedOrder && (
          <DialogContent className="p-0 flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* Header compact et professionnel - Couleur sidebar - RESPONSIVE */}
            <div className="relative bg-background-secondary p-3 sm:p-4 overflow-hidden flex-shrink-0 border-b border-border">
              {/* Bouton de fermeture */}
              <button
                onClick={() => setShowOrderModal(false)}
                className="absolute top-2 right-2 md:top-3 md:right-3 z-20 text-foreground-secondary hover:text-foreground hover:bg-background-tertiary transition-all duration-200 rounded-lg p-1.5"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-8">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-background-tertiary rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                    <span className="text-foreground font-bold text-xs sm:text-sm">
                      {selectedOrder.orderInfo.firstName[0]}{selectedOrder.orderInfo.lastName[0]}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-foreground text-sm sm:text-base truncate">
                      {/* Affichage du pseudo TikTok dans le titre si disponible */}
                      {selectedOrder.products?.[0]?.tiktokUsername ? (
                        <span className="text-primary-light">@{selectedOrder.products[0].tiktokUsername}</span>
                      ) : null}
                      {selectedOrder.products?.[0]?.tiktokUsername ? ' - ' : ''}
                      {selectedOrder.orderInfo.firstName} {selectedOrder.orderInfo.lastName}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-foreground-secondary truncate break-all">{selectedOrder.orderInfo.email}</p>
                    {selectedOrder.orderInfo.phone && (
                      <p className="text-[10px] sm:text-xs text-foreground-secondary truncate">Tél. {selectedOrder.orderInfo.phone}</p>
                    )}
                    <p className="text-[10px] sm:text-xs text-foreground-secondary font-mono truncate">ID: {selectedOrder.id?.slice(-8)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  {selectedOrder.preparationStatus && (
                    <span className={`inline-flex px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold rounded-full whitespace-nowrap ${getPreparationStatusColor(selectedOrder.preparationStatus)}`}>
                      {selectedOrder.preparationStatus === 'ready' ? 'Prêt' :
                        selectedOrder.preparationStatus === 'in_progress' ? 'En cours' : 'À faire'}
                    </span>
                  )}
                  <div className="text-right bg-background-tertiary rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-md flex-shrink-0">
                    <div className="text-sm sm:text-base md:text-xl font-black text-foreground">
                      €{(() => {
                        const productsTotal = selectedOrder.products?.reduce((sum: number, p: Product) => sum + Number.parseFloat(p.amount || '0'), 0) || 0;
                        const shippingCost = selectedOrder.deliveryInfo?.shippingCost || 0;
                        return (productsTotal + shippingCost).toFixed(2);
                      })()}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-foreground-secondary font-semibold">Total</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal moderne - SCROLLABLE - RESPONSIVE */}
            <div className="flex-1 overflow-y-auto" style={{ contentVisibility: 'auto', contain: 'layout paint style' }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6 bg-gradient-to-br from-background-tertiary to-surface min-h-full">
                {/* Produits - Colonne 1 */}
                <div>
                  <h4 className="font-bold text-foreground mb-2 sm:mb-3 flex items-center gap-2 text-xs sm:text-sm">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                      <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    Produits ({selectedOrder.products?.length || 0})
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    {selectedOrder.products?.map((product: Product, index: number) => (
                      <div key={`product-${product.reference}-${index}`} className="bg-surface rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border shadow-sm hover:shadow-md transition-all duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                              <span className="text-foreground text-xs sm:text-sm font-bold font-mono bg-background-tertiary rounded px-1.5 sm:px-2 py-0.5 truncate">{product.reference}</span>
                              <span className="text-foreground-secondary flex-shrink-0 hidden sm:inline">•</span>
                              <span className="text-foreground-secondary text-[10px] sm:text-xs">{product.createdAt ? formatDate(product.createdAt) : 'N/A'}</span>
                            </div>
                            {product.model && (
                              <div className="flex flex-col gap-0.5 sm:gap-1 mt-1">
                                {product.model.split(/\s*\+\s*/).map((part, i) => {
                                  const t = part.trim();
                                  return t ? (
                                    <span key={i} className="text-primary-dark bg-primary-10 px-2 sm:px-3 py-0.5 sm:py-1 rounded text-xs sm:text-sm font-bold inline-block w-fit">
                                      {t}
                                    </span>
                                  ) : null;
                                })}
                                {((product.reference || '').split(' ')[1]) && (
                                  <span className="text-primary-dark bg-primary-10 px-2 sm:px-3 py-0.5 sm:py-1 rounded text-xs sm:text-sm font-bold truncate inline-block w-fit">Taille {((product.reference || '').split(' ')[1])}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-left sm:text-right ml-0 sm:ml-3">
                            <div className="font-bold text-foreground text-sm sm:text-base">€{product.amount}</div>
                            <div className="flex flex-col sm:items-end gap-1.5 mt-1">
                              <span className={`inline-flex px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-bold rounded-lg ${getPaymentStatusColor(getProductDisplayStatus(selectedOrder, product, index))}`}>
                                {getPaymentStatusText(getProductDisplayStatus(selectedOrder, product, index))}
                              </span>
                              {product.paymentInfo.method && (
                                <Badge
                                  variant={getPaymentProviderInfo(product.paymentInfo.method, product).badgeVariant}
                                  size="sm"
                                  className="gap-1"
                                >
                                  {(() => {
                                    const provider = getPaymentProviderInfo(product.paymentInfo.method, product);
                                    const Icon = provider.icon;
                                    return <Icon className="w-3 h-3 flex-shrink-0" />;
                                  })()}
                                  {getPaymentProviderInfo(product.paymentInfo.method, product).name}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Raison de l'échec */}
                        {product.paymentInfo?.status === 'failed' && product.paymentInfo.failureReason && (
                          <div className="mt-2 p-2 bg-error-10 border border-error-20 rounded-lg text-xs text-error-dark">
                            <div className="font-semibold mb-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Échec du paiement
                            </div>
                            <p>{product.paymentInfo.failureReason}</p>
                            {product.paymentInfo.failureCode && (
                              <p className="text-[10px] font-mono mt-1 opacity-75">Code: {product.paymentInfo.failureCode}</p>
                            )}
                          </div>
                        )}

                        {/* Historique des tentatives de paiement */}
                        {product.paymentInfo?.paymentAttempts && Array.isArray(product.paymentInfo.paymentAttempts) && product.paymentInfo.paymentAttempts.length > 0 && (
                          <div className="mt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newExpanded = new Set(expandedHistories);
                                const key = `${selectedOrder.id || 'unknown'}-${index}`;
                                if (newExpanded.has(key)) {
                                  newExpanded.delete(key);
                                } else {
                                  newExpanded.add(key);
                                }
                                setExpandedHistories(newExpanded);
                              }}
                              className="flex items-center gap-1 text-[10px] font-bold text-foreground-secondary uppercase tracking-wider mb-2 hover:text-foreground transition-colors"
                            >
                              Historique des tentatives de paiements ({product.paymentInfo.paymentAttempts.length})
                              {expandedHistories.has(`${selectedOrder.id || 'unknown'}-${index}`) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </button>

                            {expandedHistories.has(`${selectedOrder.id || 'unknown'}-${index}`) && (
                              <div className="space-y-2">
                                {[...product.paymentInfo.paymentAttempts]
                                  .sort((a, b) => {
                                    // Robust sort handling for various timestamp formats
                                    const getTime = (t: any) => {
                                      if (!t) return 0;
                                      if (t.seconds) return t.seconds * 1000; // Firestore Timestamp object
                                      if (t.toMillis) return t.toMillis(); // Firestore Timestamp class
                                      if (typeof t === 'string') {
                                        const d = new Date(t);
                                        return isNaN(d.getTime()) ? 0 : d.getTime();
                                      }
                                      return 0;
                                    };
                                    return getTime(b.timestamp) - getTime(a.timestamp);
                                  })
                                  .map((attempt: any, i: number) => {
                                    // Déterminer la couleur en fonction du statut
                                    let bgColor = 'bg-background-tertiary';
                                    let borderColor = 'border-border';
                                    let textColor = 'text-foreground';
                                    let statusText = 'INCONNU';

                                    if (attempt.status === 'succeeded') {
                                      bgColor = 'bg-success-10';
                                      borderColor = 'border-success-20';
                                      textColor = 'text-success-dark';
                                      statusText = 'SUCCÈS';
                                    } else if (attempt.status === 'failed') {
                                      bgColor = 'bg-error-10';
                                      borderColor = 'border-error-20';
                                      textColor = 'text-error-dark';
                                      statusText = 'ÉCHEC';
                                    } else if (attempt.status === 'refunded_full' || attempt.status === 'refunded_partial') {
                                      bgColor = 'bg-secondary-10';
                                      borderColor = 'border-secondary-20';
                                      textColor = 'text-secondary-dark';
                                      statusText = attempt.status === 'refunded_full' ? 'REMBOURSÉ (TOTAL)' : 'REMBOURSÉ (PARTIEL)';
                                    }

                                    return (
                                      <div key={i} className={`text-[10px] p-2 rounded border ${bgColor} ${borderColor}`}>
                                        <div className="flex justify-between items-center mb-1">
                                          <span className={`font-bold ${textColor}`}>
                                            {statusText}
                                          </span>
                                          <span className="text-foreground-secondary">{formatDate(attempt.timestamp)}</span>
                                        </div>
                                        {(attempt.failureCode || attempt.failureReason) && (
                                          <p className="text-error mb-0.5 font-mono text-[10px]">{attempt.failureCode || attempt.failureReason}</p>
                                        )}
                                        {attempt.refundAmount && (
                                          <p className="text-secondary mb-0.5 font-semibold">Montant: €{(attempt.refundAmount / 100).toFixed(2)}</p>
                                        )}
                                        <div className="flex justify-between text-foreground-secondary font-mono text-[9px]">
                                          <span>{attempt.paymentIntentId?.slice(-8)}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const s = new Set(expandedPaymentDetails);
                              const k = `${selectedOrder.id || 'unknown'}-${index}`;
                              if (s.has(k)) s.delete(k); else s.add(k);
                              setExpandedPaymentDetails(s);
                            }}
                            className="flex items-center gap-1 text-[10px] font-bold text-foreground-secondary uppercase tracking-wider mb-2 hover:text-foreground transition-colors"
                          >
                            Information pour retrouver sur {getPaymentProviderInfo(product.paymentInfo?.method, product).name}
                            {expandedPaymentDetails.has(`${selectedOrder.id || 'unknown'}-${index}`) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                          {expandedPaymentDetails.has(`${selectedOrder.id || 'unknown'}-${index}`) && (
                            <div className="p-2 bg-background-tertiary rounded-lg space-y-1 text-[10px] font-mono text-foreground">
                              {product.paymentInfo?.paymentIntentId && (
                                <div className="flex justify-between">
                                  <span className="text-foreground-secondary font-semibold">{getPaymentProviderInfo(product.paymentInfo.method, product).idLabelShort}:</span>
                                  <span className="font-medium">{product.paymentInfo.paymentIntentId.slice(-8)}</span>
                                </div>
                              )}
                              {product.paymentInfo?.chargeId && (
                                <div className="flex justify-between">
                                  <span className="text-foreground-secondary font-semibold">CH:</span>
                                  <span className="font-medium">{product.paymentInfo.chargeId.slice(-8)}</span>
                                </div>
                              )}
                              {product.paymentInfo?.paidAt && (
                                <div className="flex justify-between pt-1 border-t border-border">
                                  <span className="text-foreground-secondary">Payé le:</span>
                                  <span className="text-foreground font-medium">{formatDate(product.paymentInfo.paidAt)}</span>
                                </div>
                              )}

                              {/* Description pour le provider */}
                              <div className="pt-2 mt-2 border-t border-border">
                                <div className="mb-1 text-[9px] font-semibold text-foreground-secondary uppercase tracking-wide">
                                  Description visible sur votre dashboard {getPaymentProviderInfo(product.paymentInfo.method, product).name}
                                </div>
                                <div className="text-[9px] text-foreground-secondary break-all leading-tight bg-background-tertiary p-1.5 rounded select-all cursor-text">
                                  {(() => {
                                    // Récupérer le nom du produit (même logique que dans WebhookController)
                                    const productName = (product as any).name || product.model || product.reference || 'Produit';

                                    // Récupérer le montant net (en centimes) ou utiliser le montant brut comme fallback
                                    const netAmountInCents = product.paymentInfo?.netAmount
                                      ? (typeof product.paymentInfo.netAmount === 'number'
                                        ? product.paymentInfo.netAmount
                                        : Math.round(Number.parseFloat((product.paymentInfo.netAmount as any).toString()) * 100))
                                      : Math.round(Number.parseFloat(product.amount || '0') * 100);

                                    // Formater en euros
                                    const netAmountFormatted = (netAmountInCents / 100).toFixed(2);

                                    // Récupérer la devise (par défaut EUR)
                                    const currency = (product.paymentInfo?.currency || 'eur').toUpperCase();

                                    // Format simplifié : Nom du produit - Commande #ID - Montant EUR
                                    return `${productName} - Commande #${selectedOrder.id?.slice(-8) || selectedOrder.id} - ${netAmountFormatted} ${currency}`;
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Reste à rembourser (partiel) : on n'affiche pas ici quand le reste = uniquement frais de livraison (voir modal "Rembourser toute la commande") */}
                        {product.paymentInfo?.status === 'refunded_partial' && (() => {
                          const remaining = getRemainingRefundableForProduct(selectedOrder, index);
                          if (remaining <= 0) return null;
                          const productAmount = Number.parseFloat(product.amount || '0') || 0;
                          const amountRefundedCents = product.amountRefunded ?? 0;
                          const isRemainingOnlyShipping = index === 0 && (amountRefundedCents / 100) >= productAmount;
                          if (isRemainingOnlyShipping) return null;
                          return (
                            <p className="mt-2 text-xs font-semibold text-secondary-dark">
                              Reste à rembourser : €{remaining.toFixed(2)}
                            </p>
                          );
                        })()}
                        {/* Remboursement par produit (Uniquement Stripe Connect) : payé OU partiel avec reste */}
                        {canManageOrdersForOrg && product.paymentInfo?.method !== 'paypal' && (product.paymentInfo?.method === 'stripe_connect' || (product.paymentInfo?.method === 'stripe' && configuration?.paymentMode === 'stripe_connect')) && (
                          (product.paymentInfo.status === 'paid' || (product.paymentInfo.status === 'refunded_partial' && getRemainingRefundableForProduct(selectedOrder, index) > 0)) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setShowOrderModal(false);
                                showCreditRefundModal(selectedOrder, index);
                              }}
                              leftIcon={<CreditCard className="w-3.5 h-3.5" />}
                              className="mt-3 w-full"
                            >
                              Rembourser ce produit
                            </Button>
                          )
                        )}

                        {product.paymentInfo.status === 'pending' && isAdminForOrg && (
                          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => showPaymentConfirmation(selectedOrder.id!, index, 'paid', product.reference)}
                              leftIcon={<CheckCircle className="w-3.5 h-3.5" />}
                              className="flex-1"
                            >
                              Payé
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => showPaymentConfirmation(selectedOrder.id!, index, 'failed', product.reference)}
                              leftIcon={<AlertCircle className="w-3.5 h-3.5" />}
                              className="flex-1"
                            >
                              Échoué
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Livraison - Colonne 2 */}
                <div>
                  <h4 className="font-bold text-foreground mb-2 sm:mb-3 flex items-center gap-2 text-xs sm:text-sm">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-success-10 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                      <Truck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    Livraison
                    {/* Alerte si commande payée mais pas de colis (parcelId = source de vérité) */}
                    {isOrderPaid(selectedOrder) && !hasTrackingOrLabel(selectedOrder) && (() => {
                      const parcelError = selectedOrder.deliveryInfo?.parcelCreationError;
                      const isInsufficientCredits = isInsufficientCreditsParcelError(selectedOrder);
                      const isParcelToGenerate = isManualGenerationPending(selectedOrder);
                      const isRealParcelError = hasRealParcelError(selectedOrder);

                      if (isRealParcelError) {
                        return (
                          <Tooltip
                            title={parcelError?.message || 'Erreur de génération du colis — voir le détail sous Livraison'}
                            placement="top"
                            arrow
                          >
                            <div className="inline-flex px-2 py-0.5 bg-error-10 border border-error-20 rounded-full">
                              <AlertCircle className="w-4 h-4 text-error animate-pulse" />
                            </div>
                          </Tooltip>
                        );
                      }

                      if (isParcelToGenerate || isInsufficientCredits) {
                        return (
                          <Tooltip
                            title="Étiquette à générer - utilisez le bouton 'Générer les colis'"
                            placement="top"
                            arrow
                          >
                            <div className="inline-flex px-2 py-0.5 bg-warning-10 border border-warning-20 rounded-full">
                              <Truck className="w-4 h-4 text-warning-dark" />
                            </div>
                          </Tooltip>
                        );
                      }

                      return (
                        <Tooltip
                          title="⚠️ Problème de création de colis SendCloud - pas de tracking disponible"
                          placement="top"
                          arrow
                        >
                          <div className="inline-flex px-2 py-0.5 bg-error-10 border border-error-20 rounded-full">
                            <AlertCircle className="w-4 h-4 text-error animate-pulse" />
                          </div>
                        </Tooltip>
                      );
                    })()}
                  </h4>
                  {selectedOrder.deliveryInfo && (
                    <div className="bg-surface rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border shadow-sm overflow-hidden">
                      {/* Livraison remboursée au client (uniquement si le montant remboursé incluait les frais de livraison) */}
                      {isShippingRefunded(selectedOrder) && (
                        <div className="p-3 mb-3 rounded-lg bg-success-10 border border-success-20">
                          <p className="text-sm font-semibold text-success-dark">
                            {t('orderDetails.shippingRefundedToClient', 'La livraison a été remboursée au client.')}
                          </p>
                        </div>
                      )}
                      {/* Erreur métier (ex. méthode manquante) — toujours visible en mode manuel */}
                      {isOrderPaid(selectedOrder) && !hasTrackingOrLabel(selectedOrder) && (() => {
                        const parcelError = selectedOrder.deliveryInfo?.parcelCreationError;
                        if (!hasRealParcelError(selectedOrder)) return null;
                        return (
                          <div className="p-3 mb-3 rounded-lg bg-error-10 border border-error-20">
                            <p className="text-sm font-bold text-error-dark">Blocage génération de colis</p>
                            <p className="text-xs text-foreground-secondary mt-1 leading-relaxed">
                              {parcelError?.message}
                            </p>
                          </div>
                        );
                      })()}
                      {/* Message d'alerte si pas de colis pour une commande payée (parcelId) */}
                      {isOrderPaid(selectedOrder) && !hasTrackingOrLabel(selectedOrder) && (() => {
                        const parcelError = selectedOrder.deliveryInfo?.parcelCreationError;
                        const isInsufficientCredits = isInsufficientCreditsParcelError(selectedOrder);
                        const isRealParcelError = hasRealParcelError(selectedOrder);

                        // Mode API Key/PayPal OU crédits insuffisants: Bouton pour générer le colis
                        if (isManualGenerationPending(selectedOrder) || isInsufficientCredits) {
                          return (
                            <div className="p-4 bg-warning-10 border border-warning-20 rounded-xl mb-3 sm:mb-4 shadow-sm">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-warning-5 rounded-lg flex-shrink-0">
                                  <Truck className="w-5 h-5 text-warning-dark" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-warning-dark mb-1">
                                    Étiquette de livraison à générer
                                  </p>
                                  <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() => {
                                      // Close the modal
                                      setShowOrderModal(false);
                                      // Enter batch selection mode
                                      setIsBatchSelectionMode(true);
                                      // Select this order
                                      if (selectedOrder?.id) {
                                        setSelectedOrderIds(new Set([selectedOrder.id]));
                                      }
                                    }}
                                    leftIcon={<Truck className="w-4 h-4" />}
                                    className="mt-2"
                                  >
                                    Générer le colis
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (isRealParcelError) return null;

                        return null; // Le formulaire d'erreur Stripe Connect est affiché plus bas
                      })()}

                      {/* Alerte et bouton de résolution uniquement en cas de vrai problème (pas pour "colis à générer") */}
                      {isOrderPaid(selectedOrder) && !hasTrackingOrLabel(selectedOrder) && (() => {
                        const parcelError = selectedOrder.deliveryInfo?.parcelCreationError;
                        const isParcelToGenerate = isManualGenerationPending(selectedOrder);
                        // Ne pas afficher ce bloc si c'est simplement un colis à générer (déjà géré au-dessus)
                        if (isParcelToGenerate) return null;

                        const isRealParcelError = hasRealParcelError(selectedOrder);
                        return (
                          <div className="p-4 bg-background-tertiary border border-error/20 rounded-xl mb-4 shadow-sm">
                            <div className="flex items-start gap-3">
                              <div className="p-2 bg-error/10 rounded-lg flex-shrink-0">
                                <AlertCircle className="w-5 h-5 text-error" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-sm text-foreground">
                                  {isRealParcelError ? 'Problème lors de la création du colis' : 'Colis en attente'}
                                </h4>
                                <p className="text-xs text-foreground-secondary mt-1 mb-3 leading-relaxed">
                                  {parcelError?.message || "Le colis n'a pas été généré automatiquement. Une action manuelle est requise pour valider l'expédition."}
                                </p>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => openAddressModal(selectedOrder)}
                                  leftIcon={<Edit className="w-4 h-4" />}
                                  className="w-full sm:w-auto font-bold shadow-lg shadow-primary/20"
                                >
                                  Résoudre le problème de livraison
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0 p-2 bg-background-tertiary rounded-lg">
                          <span className="text-foreground-secondary text-[10px] sm:text-xs font-medium">Méthode / Fournisseur:</span>
                          <span className="text-foreground text-[10px] sm:text-xs font-semibold break-words text-right sm:text-left">
                            {(() => {
                              const di = selectedOrder.deliveryInfo;
                              const idLabel = resolveDashboardShippingMethodId(di);
                              const methodLabel = di.methodName || (idLabel ? `Méthode ${idLabel}` : 'Non spécifié');
                              const providerLabel = di.provider ? String(di.provider) : null;
                              return providerLabel ? `${methodLabel} — ${providerLabel}` : methodLabel;
                            })()}
                          </span>
                        </div>

                        {selectedOrder.deliveryInfo.estimatedHours &&
                          String(selectedOrder.deliveryInfo.provider || '').toLowerCase() !== 'mondial_relay' && (
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0 p-2 bg-background-tertiary rounded-lg">
                            <span className="text-foreground-secondary text-[10px] sm:text-xs font-medium">Délai:</span>
                            <span className="text-foreground text-[10px] sm:text-xs font-semibold">
                              {selectedOrder.deliveryInfo.estimatedHours}h
                            </span>
                          </div>
                        )}

                        {/* Adresse du domicile - toujours affichée */}
                        {selectedOrder.deliveryInfo.address && (
                          <div className="p-2.5 sm:p-3 bg-background-tertiary rounded-lg border border-primary-20 overflow-hidden">
                            <div className="flex items-start mb-1">
                              <span className="text-primary-dark text-[10px] sm:text-xs font-bold block">Adresse de livraison:</span>
                            </div>
                            <p className="text-foreground text-[10px] sm:text-xs leading-relaxed break-words">
                              {[selectedOrder.deliveryInfo.houseNumber, selectedOrder.deliveryInfo.address].filter(Boolean).join(' ')}<br />
                              {[selectedOrder.deliveryInfo.city, selectedOrder.deliveryInfo.country, selectedOrder.deliveryInfo.postalCode].filter(Boolean).join(' ')}
                            </p>
                          </div>
                        )}

                        {/* Point relais si sélectionné - affiché en plus */}
                        {selectedOrder.deliveryInfo.pickupPoint && (
                          <div className="p-2.5 sm:p-3 bg-background-tertiary rounded-lg border border-warning-20 overflow-hidden">
                            <span className="text-warning-dark text-[10px] sm:text-xs font-bold block mb-1">Point relais:</span>
                            <p className="text-foreground text-[10px] sm:text-xs leading-relaxed break-words">
                              <strong className="break-words">{selectedOrder.deliveryInfo.pickupPoint.name}</strong><br />
                              {selectedOrder.deliveryInfo.pickupPoint.address}<br />
                              {selectedOrder.deliveryInfo.pickupPoint.postalCode} {selectedOrder.deliveryInfo.pickupPoint.city}
                            </p>
                          </div>
                        )}

                        {/* Livraison : séparée entre choix client et coût vendeur réel */}
                        {(() => {
                          const di: any = selectedOrder.deliveryInfo || {};
                          const pg: any = (selectedOrder as any).parcelGenerated || {};
                          const customerPaidEuros =
                            typeof di.customShippingPriceCents === 'number'
                              ? di.customShippingPriceCents / 100
                              : Number(di.shippingCost || 0);
                          const customerMethodPrice =
                            typeof di.methodPrice === 'number' ? di.methodPrice : null;
                          const sellerMethodPrice =
                            typeof pg.methodPrice === 'number' ? Number(pg.methodPrice) : null;
                          const sellerActualEurosRaw =
                            typeof pg.sendcloudPriceWithMarkupCents === 'number'
                              ? pg.sendcloudPriceWithMarkupCents / 100
                              : sellerMethodPrice;
                          const sellerProvider = String(pg.provider || di.provider || '').toLowerCase();
                          const isSellerCostUnknown = sellerProvider === 'mondial_relay';
                          const sellerActualEuros = isSellerCostUnknown ? null : sellerActualEurosRaw;

                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div className="p-2.5 sm:p-3 rounded-lg border border-primary-20 bg-primary-5">
                                <p className="text-[10px] sm:text-xs font-bold text-primary-dark">Livraison — choix client</p>
                                <p className="mt-1 text-[10px] sm:text-xs text-foreground-secondary">
                                  Montant payé par le client
                                </p>
                                <p className="font-black text-foreground text-sm sm:text-base">
                                  €{customerPaidEuros.toFixed(2)}
                                </p>
                                <p className="mt-1 text-[10px] text-foreground-secondary">
                                  Méthode affichée client : {customerMethodPrice != null ? `€${customerMethodPrice.toFixed(2)}` : '—'}
                                </p>
                              </div>

                              <div className="p-2.5 sm:p-3 rounded-lg border border-secondary-20 bg-secondary-5">
                                <p className="text-[10px] sm:text-xs font-bold text-secondary-dark">Livraison — colis créé (vendeur)</p>
                                <p className="mt-1 text-[10px] sm:text-xs text-foreground-secondary">
                                  Montant méthode du colis généré
                                </p>
                                <p className="font-black text-foreground text-sm sm:text-base">
                                  {sellerActualEuros != null ? `€${sellerActualEuros.toFixed(2)}` : '—'}
                                </p>
                                <p className="mt-1 text-[10px] text-foreground-secondary">
                                  {isSellerCostUnknown
                                    ? 'Coût transporteur non connu (Mondial Relay direct)'
                                    : pg.trackingNumber
                                      ? `Tracking: ${pg.trackingNumber}`
                                      : 'Colis non généré'}
                                </p>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Reste à rembourser (frais de livraison uniquement) — visible ici pour inciter à utiliser "Rembourser tout" */}
                        {(() => {
                          const shippingCost = selectedOrder.deliveryInfo?.shippingCost ?? 0;
                          const hasSendcloudRefund = !!selectedOrder.deliveryInfo?.sendcloudRefund;
                          const canRefundShipping = shippingCost > 0 && !hasSendcloudRefund;
                          const product0 = selectedOrder.products?.[0];
                          const amountRefunded0 = (product0?.amountRefunded ?? 0) / 100;
                          const product0Amount = Number.parseFloat(product0?.amount || '0') || 0;
                          const isRemainingOnlyShipping =
                            canRefundShipping &&
                            product0?.paymentInfo?.status === 'refunded_partial' &&
                            amountRefunded0 >= product0Amount;
                          if (!isRemainingOnlyShipping) return null;
                          return (
                            <div className="p-2.5 sm:p-3 rounded-lg border border-secondary-20 bg-secondary-10">
                              <p className="text-[10px] sm:text-xs font-semibold text-secondary-dark">
                                Reste à rembourser (frais de livraison) : €{shippingCost.toFixed(2)}
                              </p>
                              <p className="text-[10px] sm:text-xs text-foreground-secondary mt-0.5">
                                Utilisez le bouton « Rembourser tout » ci-dessous et cochez « Inclure les frais de livraison ».
                              </p>
                            </div>
                          );
                        })()}

                        {/* Période de fusion : savoir si la commande est "ouverte" frais de livraison une fois pendant la période */}
                        {(() => {
                          const snap = (selectedOrder as FirebaseOrder & { mergeConfigSnapshot?: { periodStart?: string; periodEnd?: string; wasCreatedInOpenDays?: boolean } | null }).mergeConfigSnapshot;
                          if (!snap) return null;
                          const periodStart = snap.periodStart ? new Date(snap.periodStart) : null;
                          const periodEnd = snap.periodEnd ? new Date(snap.periodEnd) : null;
                          const now = new Date();
                          const isPeriodStillOpen = periodStart && periodEnd && now >= periodStart && now <= periodEnd;
                          const formatPeriodDate = (d: Date) => {
                            const day = d.getDate().toString().padStart(2, '0');
                            const month = (d.getMonth() + 1).toString().padStart(2, '0');
                            const year = d.getFullYear();
                            const h = d.getHours().toString().padStart(2, '0');
                            const m = d.getMinutes().toString().padStart(2, '0');
                            return `${day}/${month}/${year} ${h}:${m}`;
                          };
                          return (
                            <div className="p-2.5 sm:p-3 rounded-lg border border-border bg-background-tertiary/50 space-y-1.5">
                              <p className="text-[10px] sm:text-xs font-bold text-foreground">
                                {t('admin.dashboard.orderMerge.periodTitle', 'Frais de livraison – Période de fusion')}
                              </p>
                              {snap.wasCreatedInOpenDays ? (
                                <>
                                  <p className="text-[10px] sm:text-xs text-foreground-secondary">
                                    {t('admin.dashboard.orderMerge.createdInOpenDays', 'Commande créée dans une plage de jours ouverts.')}
                                  </p>
                                  {periodStart && periodEnd && (
                                    <p className="text-[10px] sm:text-xs text-foreground-secondary font-mono">
                                      {t('admin.dashboard.orderMerge.periodRange', 'Période')}: {formatPeriodDate(periodStart)} → {formatPeriodDate(periodEnd)}
                                    </p>
                                  )}
                                  {isPeriodStillOpen ? (
                                    <>
                                      {selectedOrder && isShippingRefunded(selectedOrder) ? (
                                        <p className="text-[10px] sm:text-xs font-semibold text-foreground-secondary px-2 py-1 rounded bg-background-tertiary">
                                          {t('admin.dashboard.orderMerge.periodClosedRefunded', 'Période fermée. Les frais de livraison ont été remboursés.')}
                                        </p>
                                      ) : (
                                        <>
                                          <p className="text-[10px] sm:text-xs font-semibold text-success-dark bg-success-10 px-2 py-1 rounded">
                                            {t('admin.dashboard.orderMerge.periodOpen', 'Période encore ouverte : le client ne repaie pas la livraison pour une nouvelle commande.')}
                                          </p>
                                          {canManageOrdersForOrg && selectedOrder && (
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={async () => {
                                            if (!selectedOrder?.id) return;
                                            setIsClosingMergePeriod(true);
                                            try {
                                              await OrderRepository.closeOrderMergePeriod(selectedOrder.id);
                                              const currentSnap = (selectedOrder as FirebaseOrder & { mergeConfigSnapshot?: { periodStart?: string; periodEnd?: string; wasCreatedInOpenDays?: boolean } | null }).mergeConfigSnapshot;
                                              const nowIso = new Date().toISOString();
                                              setSelectedOrder((prev) =>
                                                prev
                                                  ? {
                                                      ...prev,
                                                      mergeConfigSnapshot: currentSnap
                                                        ? { ...currentSnap, periodEnd: nowIso }
                                                        : { periodStart: nowIso, periodEnd: nowIso, wasCreatedInOpenDays: false },
                                                    }
                                                  : null
                                              );
                                              toast.success(t('admin.dashboard.orderMerge.periodClosedSuccess', 'Période clôturée. Le client repaiera les frais de livraison pour une prochaine commande.'));
                                            } catch (err) {
                                              console.error(err);
                                              toast.error(t('admin.dashboard.orderMerge.periodClosedError', 'Impossible de clôturer la période.'));
                                            } finally {
                                              setIsClosingMergePeriod(false);
                                            }
                                          }}
                                          disabled={isClosingMergePeriod}
                                          isLoading={isClosingMergePeriod}
                                          className="mt-2 w-full sm:w-auto text-xs"
                                        >
                                          {t('admin.dashboard.orderMerge.closePeriod', 'Clôturer la période')}
                                        </Button>
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <p className="text-[10px] sm:text-xs text-foreground-secondary">
                                      {t('admin.dashboard.orderMerge.periodClosed', 'Période fermée.')}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="text-[10px] sm:text-xs text-foreground-secondary">
                                  {t('admin.dashboard.orderMerge.sameDayOnly', 'Frais de livraison une fois le même jour uniquement.')}
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Statut du remboursement des frais de livraison / Sendcloud */}
                        {selectedOrder.deliveryInfo?.sendcloudRefund && (
                          <div className="p-3 bg-background-tertiary rounded-lg border border-secondary-20">
                            {(() => {
                              const refund = selectedOrder.deliveryInfo.sendcloudRefund!;
                              const status = refund.status || 'inconnu';
                              const isLabelRefund = refund.type === 'label_refund';

                              let badgeColor = 'bg-background-tertiary text-foreground';
                              let label = t('orderDetails.labelAndShippingRefunded', 'Label et frais de livraison remboursés');

                              if (status === 'completed') {
                                badgeColor = 'bg-success-10 text-success-dark';
                                label = isLabelRefund
                                  ? t('orderDetails.labelRefundedBySendcloud', 'Le label a été remboursé par Sendcloud. Vous ne nous devez rien.')
                                  : t('orderDetails.labelAndShippingRefunded', 'Label et frais de livraison remboursés');
                              } else if (status === 'sendcloud_only') {
                                badgeColor = 'bg-primary-10 text-primary-dark';
                                label = t(
                                  'orderDetails.labelSendcloudOnly',
                                  'Label Sendcloud remboursé (crédit transporteur uniquement)'
                                );
                              } else if (status === 'partial') {
                                badgeColor = 'bg-warning-10 text-warning-dark';
                                label = t(
                                  'orderDetails.labelNotRefundedSendcloud',
                                  'Le label n\'a pas été remboursé par Sendcloud (non éligible ou erreur).'
                                );
                              }

                              const shippingAmount =
                                typeof refund.shippingRefundAmount === 'number'
                                  ? (refund.shippingRefundAmount / 100).toFixed(2)
                                  : null;

                              return (
                                <div className="space-y-2 text-xs overflow-hidden">
                                  <span className={`inline-flex px-3 py-1.5 rounded-lg font-bold ${badgeColor}`}>
                                    {label}
                                  </span>
                                  {shippingAmount && Number(refund.shippingRefundAmount) > 0 && (
                                    <p className="text-foreground font-medium break-words">
                                      {t('orderDetails.shippingRefundAmount', 'Frais de livraison remboursés')}:{' '}
                                      <span className="font-black text-foreground">{shippingAmount} €</span>
                                    </p>
                                  )}
                                  {refund.error && (
                                    <p className="text-error font-medium break-words overflow-hidden">
                                      {t('orderDetails.sendcloudError', 'Erreur Sendcloud')}:{" "}
                                      <span className="font-bold break-all">{refund.error}</span>
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}

                        {/* Bouton de suivi de livraison */}
                        {isOrderPaid(selectedOrder) && getOrderTrackingUrl(selectedOrder) && (
                          <a
                            href={getOrderTrackingUrl(selectedOrder) || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full"
                          >
                            <Button
                              variant="secondary"
                              size="md"
                              leftIcon={<ExternalLink className="w-4 h-4" />}
                              className="w-full text-xs sm:text-sm"
                            >
                              Suivre la livraison
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Fin colonnes */}
              </div>
            </div>


            {/* Actions rapides modernes - Thème Cashlive - FIXE - RESPONSIVE */}
            <div className="bg-gradient-to-r from-background-secondary to-surface px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-border flex-shrink-0">
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center sm:justify-start">
                {/* Actions de statut - Uniquement pour les super admins */}
                {/* Actions de statut - Uniquement pour les super admins */}
                {/* Les actions de statut globales sont désactivées car gérées par produit */}

                {/* Actions de suivi */}
                {canManageOrdersForOrg && isOrderPaid(selectedOrder) && getOrderLabelUrl(selectedOrder) && (
                  <>
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => printLabel(selectedOrder)}
                      leftIcon={<Truck className="w-4 h-4" />}
                      className="w-full sm:w-auto"
                    >
                      <span className="hidden sm:inline">Imprimer</span>
                      <span className="sm:hidden">Imprimer</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => downloadLabel(selectedOrder)}
                      leftIcon={<Download className="w-4 h-4" />}
                      className="w-full sm:w-auto"
                    >
                      <span className="hidden sm:inline">Télécharger</span>
                      <span className="sm:hidden">Télécharger</span>
                    </Button>
                  </>
                )}

                {/* (supprimé) Création de retour Sendcloud */}

                {/* Action remboursement complet - Stripe Connect : au moins un produit payé OU un reste à rembourser (ex. frais de livraison) */}
                {canManageOrdersForOrg && configuration?.paymentMode === 'stripe_connect' && (() => {
                  const hasPaidProduct = selectedOrder.products?.some((p: any) => p.paymentInfo?.status === 'paid' && p.paymentInfo?.method !== 'paypal');
                  const remainingTotal = (selectedOrder.products?.length ?? 0) > 0
                    ? selectedOrder.products!.reduce(
                        (sum: number, _p: any, i: number) => sum + getRemainingRefundableForProduct(selectedOrder, i),
                        0
                      )
                    : 0;
                  return (hasPaidProduct || remainingTotal > 0) ? (
                    <Button
                      variant="danger"
                      size="md"
                      onClick={() => {
                        if (!selectedOrder) return;
                        openFullRefundModal(selectedOrder);
                      }}
                      leftIcon={<CreditCard className="w-4 h-4" />}
                      className="w-full sm:w-auto md:ml-auto"
                    >
                      Rembourser tout
                    </Button>
                  ) : null;
                })()}
              </div>
            </div>
          </DialogContent>
        )}
      </TailwindDialog>

      {/* Full Order Refund Confirmation Modal */}
      <TailwindDialog
        isOpen={showFullRefundModal && !!fullRefundOrder}
        onClose={() => {
          setShowFullRefundModal(false);
          setFullRefundOrder(null);
          setFullRefundIncludeShipping(false);
        }}
        title="Rembourser toute la commande"
        maxWidth="md"
        variant="themed"
      >
        {fullRefundOrder && (
          <>
            <DialogContent>
              {(() => {
                const paidProducts = fullRefundOrder.products?.filter(
                  (p: Product) => p.paymentInfo?.status === 'paid'
                ) || [];
                const productsTotal = paidProducts.reduce(
                  (sum: number, p: Product) => sum + Number.parseFloat(p.amount || '0'),
                  0
                );
                const shippingCost = fullRefundOrder.deliveryInfo?.shippingCost || 0;
                const hasSendcloudRefund = !!fullRefundOrder.deliveryInfo?.sendcloudRefund;
                const canTryShippingRefund =
                  shippingCost > 0 && !hasSendcloudRefund;
                const shippingProvider = String(
                  fullRefundOrder.parcelGenerated?.provider ||
                  fullRefundOrder.deliveryInfo?.provider ||
                  ''
                ).toLowerCase();
                const isSellerSendcloud = shippingProvider === 'sendcloud_api_key';

                const includeShipping = fullRefundIncludeShipping && canTryShippingRefund;
                const remainingTotal = (fullRefundOrder.products?.length ?? 0) > 0
                  ? fullRefundOrder.products!.reduce(
                      (sum: number, _p: Product, i: number) => sum + getRemainingRefundableForProduct(fullRefundOrder, i),
                      0
                    )
                  : 0;
                const product0 = fullRefundOrder.products?.[0];
                const amountRefunded0 = (product0?.amountRefunded ?? 0) / 100;
                const product0Amount = Number.parseFloat(product0?.amount || '0') || 0;
                const isRemainingOnlyShipping =
                  remainingTotal > 0 &&
                  canTryShippingRefund &&
                  remainingTotal <= shippingCost + 1e-6 &&
                  fullRefundOrder.products?.length &&
                  fullRefundOrder.products[0]?.paymentInfo?.status === 'refunded_partial' &&
                  amountRefunded0 >= product0Amount;

                const indicesToRefund = fullRefundOrder.products?.map((_p: Product, i: number) => i).filter(
                  (i: number) => {
                    const pr = fullRefundOrder.products![i];
                    return (pr.paymentInfo?.status === 'paid' && pr.paymentInfo?.paymentIntentId) ||
                      (pr.paymentInfo?.status === 'refunded_partial' && pr.paymentInfo?.paymentIntentId &&
                        getRemainingRefundableForProduct(fullRefundOrder, i) > 0);
                  }
                ) ?? [];
                const totalRefunded = remainingTotal > 0
                  ? (includeShipping
                    ? remainingTotal
                    : indicesToRefund.reduce(
                        (s: number, i: number) =>
                          s + (i === 0 && canTryShippingRefund
                            ? Math.max(0, getRemainingRefundableForProduct(fullRefundOrder, 0) - shippingCost)
                            : getRemainingRefundableForProduct(fullRefundOrder, i)),
                        0
                      ))
                  : (includeShipping ? productsTotal + shippingCost : productsTotal);

                return (
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-6 w-6 text-error" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-foreground font-semibold">
                          Vous êtes sur le point de rembourser toute la commande.
                        </p>
                        <p className="text-xs text-foreground-secondary mt-1">
                          Les produits payés seront remboursés sur la carte du client. La commission de la plateforme ne sera pas remboursée.
                        </p>
                      </div>
                    </div>

                    {remainingTotal > 0 && (
                      <div className="p-3 bg-secondary-10 border border-secondary-20 rounded-lg">
                        <p className="text-sm font-semibold text-secondary-dark">
                          Reste à rembourser : €{remainingTotal.toFixed(2)}
                          {isRemainingOnlyShipping && (
                            <span className="block text-xs font-normal mt-0.5">
                              (frais de livraison)
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    {canTryShippingRefund && (
                      <div className="p-3 bg-primary-10 border border-primary-20 rounded-lg">
                        <label className="flex items-start cursor-pointer gap-2">
                          <input
                            type="checkbox"
                            checked={fullRefundIncludeShipping}
                            onChange={(e) => setFullRefundIncludeShipping(e.target.checked)}
                            disabled={processingRefund}
                            className="mt-0.5 h-4 w-4 text-primary focus:ring-primary border-border-medium rounded bg-background"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              Inclure les frais de livraison dans le remboursement
                            </p>
                            <p className="text-xs text-foreground-secondary mt-0.5">
                              {isSellerSendcloud
                                ? `Si coché, le client sera remboursé aussi des frais de livraison (€${shippingCost.toFixed(2)}). La demande d'annulation du label d'expédition est ensuite envoyée à Sendcloud ; si elle est acceptée, le montant est recrédité automatiquement sur leur compte Sendcloud.`
                                : `Si coché, le client sera remboursé aussi des frais de livraison (€${shippingCost.toFixed(2)}). La demande d'annulation du label d'expédition est ensuite envoyée à Sendcloud ; si elle est acceptée, le montant est recrédité automatiquement dans les crédits plateforme.`}
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    <div className="bg-background-tertiary border border-border rounded-lg p-3 text-sm">
                      {remainingTotal > 0 ? (
                        <>
                          {includeShipping && (
                            <>
                              {!isRemainingOnlyShipping && (
                                <div className="flex justify-between mb-1">
                                  <span className="text-foreground-secondary">Reste produits:</span>
                                  <span className="font-semibold text-foreground">
                                    €{(remainingTotal - shippingCost).toFixed(2)}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between mb-1">
                                <span className="text-foreground-secondary">Frais de livraison:</span>
                                <span className="font-semibold text-foreground">
                                  €{shippingCost.toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                          {!includeShipping && (
                            <div className="flex justify-between mb-1">
                              <span className="text-foreground-secondary">Reste à rembourser:</span>
                              <span className="font-semibold text-foreground">
                                €{totalRefunded.toFixed(2)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between mb-1">
                            <span className="text-foreground-secondary">Produits:</span>
                            <span className="font-semibold text-foreground">
                              €{productsTotal.toFixed(2)}
                            </span>
                          </div>
                          {includeShipping && (
                            <div className="flex justify-between mb-1">
                              <span className="text-foreground-secondary">Frais de livraison:</span>
                              <span className="font-semibold text-foreground">
                                €{shippingCost.toFixed(2)}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between pt-1 mt-1 border-t border-border">
                        <span className="text-foreground font-medium">Total remboursé:</span>
                        <span className="font-bold text-foreground">
                          €{totalRefunded.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {hasSendcloudRefund && (
                      <div className="p-3 bg-warning-10 border border-warning-20 rounded-lg text-xs text-warning-dark">
                        Une tentative de remboursement des frais de livraison a déjà été effectuée pour cette commande.
                        Ils ne pourront pas être redemandés dans ce remboursement complet.
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setShowFullRefundModal(false);
                  setFullRefundOrder(null);
                  setFullRefundIncludeShipping(false);
                }}
                disabled={processingRefund}
              >
                Annuler
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={async () => {
                  if (!fullRefundOrder || processingRefund) return;

                  const shippingCost = fullRefundOrder.deliveryInfo?.shippingCost || 0;
                  const hasSendcloudRefund = !!fullRefundOrder.deliveryInfo?.sendcloudRefund;
                  const canTryShippingRefund =
                    shippingCost > 0 && !hasSendcloudRefund;

                  await handleFullOrderRefund(
                    fullRefundOrder,
                    fullRefundIncludeShipping && canTryShippingRefund
                  );
                  setShowFullRefundModal(false);
                  setFullRefundOrder(null);
                  setFullRefundIncludeShipping(false);
                  setShowOrderModal(false);
                }}
                disabled={processingRefund}
                isLoading={processingRefund}
              >
                {processingRefund ? 'Remboursement en cours...' : 'Confirmer le remboursement'}
              </Button>
            </DialogActions>
          </>
        )}
      </TailwindDialog>

      {/* Confirmation Modal */}
      <TailwindDialog
        isOpen={showConfirmation && !!confirmationAction}
        onClose={() => {
          setShowConfirmation(false);
          setConfirmationAction(null);
        }}
        title={t('admin.dashboard.confirmation.title')}
        maxWidth="md"
        variant="themed"
      >
        {confirmationAction && (
          <>
            <DialogContent>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  {confirmationAction.type === 'paid' ? (
                    <CheckCircle className="h-6 w-6 text-success" />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-error" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm text-foreground-secondary">
                    Êtes-vous sûr de vouloir marquer le produit{' '}
                    <span className="font-medium text-foreground">
                      "{confirmationAction.productReference}"
                    </span>{' '}
                    comme{' '}
                    <span className={`font-medium ${confirmationAction.type === 'paid' ? 'text-success' : 'text-error'}`}>
                      {confirmationAction.type === 'paid' ? 'payé' : 'échoué'}
                    </span>
                    ?
                  </p>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmationAction(null);
                }}
              >
                Annuler
              </Button>
              <Button
                variant={confirmationAction.type === 'paid' ? 'primary' : 'danger'}
                size="md"
                onClick={handleConfirmAction}
              >
                {t('common.confirm')}
              </Button>
            </DialogActions>
          </>
        )}
      </TailwindDialog>

      {/* Credit/Refund Modal */}
      <TailwindDialog
        isOpen={showCreditModal && !!creditOrder}
        onClose={() => {
          setShowCreditModal(false);
          setCreditOrder(null);
          setCreditAmount('');
          setCreditDescription('');
          setIncludeShipping(false);
        }}
        title="Remboursement"
        maxWidth="md"
        variant="themed"
      >
        {creditOrder && (
          <>
            <DialogContent>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <CreditCard className="h-6 w-6 text-secondary" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-foreground-secondary">
                    Remboursement pour{' '}
                    <span className="font-medium text-foreground">
                      {creditOrder.orderInfo.email}
                    </span>
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Sélection du produit à rembourser (si plusieurs produits) */}
                {creditOrder && creditOrder.products && creditOrder.products.length > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Produit à rembourser
                    </label>
                    <select
                      value={creditProductIndex}
                      onChange={(e) => {
                        const newIndex = Number.parseInt(e.target.value, 10);
                        setCreditProductIndex(newIndex);
                        const product = creditOrder.products?.[newIndex];
                        const productAmount = product ? Number.parseFloat(product.amount || '0') : 0;
                        setCreditAmount(productAmount > 0 ? productAmount.toFixed(2) : '');
                      }}
                      disabled={processingRefund}
                      className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-background-tertiary disabled:cursor-not-allowed text-sm"
                    >
                      {creditOrder.products.map((product: Product, index: number) => (
                        <option key={index} value={index}>
                          @{product.tiktokUsername} - {product.reference} - €{Number.parseFloat(product.amount || '0').toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Option pour inclure les frais de livraison
                    👉 Règle métier: on ne peut rembourser les frais de livraison
                    que si l'intégralité de la commande est remboursée.
                    Comme le remboursement direct porte sur un seul produit,
                    on n'autorise includeShipping que pour les commandes à 1 seul produit.
                    ❌ Si une tentative de remboursement des frais de livraison (Sendcloud) a déjà été faite,
                    on ne repropose plus cette option (présence de deliveryInfo.sendcloudRefund). */}
                {creditOrder &&
                  creditOrder.products?.[creditProductIndex] &&
                  creditOrder.products.length === 1 &&
                  creditOrder.deliveryInfo?.shippingCost &&
                  creditOrder.deliveryInfo.shippingCost > 0 &&
                  !creditOrder.deliveryInfo.sendcloudRefund &&
                  (() => {
                    const product = creditOrder.products[creditProductIndex];
                    const productAmount = Number.parseFloat(product.amount || '0');
                    const shippingCost = creditOrder.deliveryInfo.shippingCost || 0;
                    const totalAmount = productAmount + shippingCost;
                    const alreadyRefundedInCents = product.amountRefunded || 0;
                    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                      ? alreadyRefundedInCents / 100
                      : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                    const maxRefundableWithShipping = totalAmount - alreadyRefunded;

                    return (
                      <div className="mb-3 p-3 bg-primary-10 border border-primary-20 rounded-lg">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeShipping}
                            onChange={(e) => {
                              setIncludeShipping(e.target.checked);
                              if (e.target.checked) {
                                // Si on coche, mettre le montant total (produit + livraison)
                                setCreditAmount(maxRefundableWithShipping > 0 ? maxRefundableWithShipping.toFixed(2) : totalAmount.toFixed(2));
                              } else {
                                // Si on décoche, remettre seulement le montant du produit
                                const maxRefundableProduct = productAmount - alreadyRefunded;
                                setCreditAmount(maxRefundableProduct > 0 ? maxRefundableProduct.toFixed(2) : productAmount.toFixed(2));
                              }
                            }}
                            disabled={processingRefund}
                            className="mr-2 h-4 w-4 text-primary focus:ring-primary border-border-medium rounded bg-background"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-foreground">
                              Inclure les frais de livraison dans le remboursement
                            </span>
                            <p className="text-xs text-foreground-secondary mt-0.5">
                              Produit: €{productAmount.toFixed(2)} + Livraison: €{shippingCost.toFixed(2)} = Total: €{totalAmount.toFixed(2)}
                              {alreadyRefunded > 0 && ` (déjà remboursé: €${alreadyRefunded.toFixed(2)})`}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })()}

                <div>
                  <label htmlFor="creditAmount" className="block text-sm font-medium text-foreground mb-1">
                    Montant (€)
                    {creditOrder && creditOrder.products?.[creditProductIndex] && (() => {
                      const product = creditOrder.products[creditProductIndex];
                      const productAmount = Number.parseFloat(product.amount || '0');
                      const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
                      const totalAmount = productAmount + shippingCost;
                      // amountRefunded est stocké en centimes dans Firestore
                      const alreadyRefundedInCents = product.amountRefunded || 0;
                      const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                        ? alreadyRefundedInCents / 100
                        : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                      const maxRefundable = includeShipping
                        ? totalAmount - alreadyRefunded
                        : productAmount - alreadyRefunded;
                      if (maxRefundable > 0) {
                        return (
                          <span className="text-xs font-normal text-foreground-secondary ml-2">
                            (Max: €{maxRefundable.toFixed(2)})
                            {alreadyRefunded > 0 && `, déjà remboursé: €${alreadyRefunded.toFixed(2)}`})
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </label>
                  <input
                    type="number"
                    id="creditAmount"
                    step="0.01"
                    min="0.01"
                    max={creditOrder?.products?.[creditProductIndex] ? (() => {
                      const product = creditOrder.products[creditProductIndex];
                      const productAmount = Number.parseFloat(product.amount || '0');
                      const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
                      const totalAmount = productAmount + shippingCost;
                      // amountRefunded est stocké en centimes dans Firestore
                      const alreadyRefundedInCents = product.amountRefunded || 0;
                      const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                        ? alreadyRefundedInCents / 100
                        : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                      return includeShipping
                        ? totalAmount - alreadyRefunded
                        : productAmount - alreadyRefunded;
                    })() : undefined}
                    value={creditAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreditAmount(value);

                      // Validation en temps réel
                      if (creditOrder?.products?.[creditProductIndex]) {
                        const product = creditOrder.products[creditProductIndex];
                        const productAmount = Number.parseFloat(product.amount || '0');
                        const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
                        const totalAmount = productAmount + shippingCost;
                        // amountRefunded est stocké en centimes dans Firestore
                        const alreadyRefundedInCents = product.amountRefunded || 0;
                        const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                          ? alreadyRefundedInCents / 100
                          : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                        const maxRefundable = includeShipping
                          ? totalAmount - alreadyRefunded
                          : productAmount - alreadyRefunded;
                        const enteredAmount = Number.parseFloat(value);

                        if (!Number.isNaN(enteredAmount) && enteredAmount > maxRefundable) {
                          // Ne pas bloquer la saisie, mais le backend validera
                        }
                      }
                    }}
                    disabled={processingRefund}
                    className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-background-tertiary disabled:cursor-not-allowed"
                    placeholder="0.00"
                    autoFocus
                  />
                  {creditOrder?.products?.[creditProductIndex] && (() => {
                    const product = creditOrder.products[creditProductIndex];
                    const productAmount = Number.parseFloat(product.amount || '0');
                    const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
                    const totalAmount = productAmount + shippingCost;
                    // amountRefunded est stocké en centimes dans Firestore
                    const alreadyRefundedInCents = product.amountRefunded || 0;
                    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                      ? alreadyRefundedInCents / 100
                      : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                    const maxRefundable = includeShipping
                      ? totalAmount - alreadyRefunded
                      : productAmount - alreadyRefunded;
                    const enteredAmount = Number.parseFloat(creditAmount || '0');

                    if (!Number.isNaN(enteredAmount) && enteredAmount > maxRefundable && maxRefundable > 0) {
                      return (
                        <p className="mt-1 text-xs text-error">
                          ⚠️ Le montant dépasse le montant restant remboursable (€{maxRefundable.toFixed(2)}).
                          Montant {includeShipping ? 'total (produit + livraison)' : 'du produit'}: €{totalAmount.toFixed(2)}, déjà remboursé: €{alreadyRefunded.toFixed(2)}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div>
                  <label htmlFor="creditDescription" className="block text-sm font-medium text-foreground mb-1">
                    {t('admin.dashboard.credit.description')}
                  </label>
                  <input
                    type="text"
                    id="creditDescription"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    disabled={processingRefund}
                    className="w-full px-3 py-2 border border-border-medium rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-background-tertiary disabled:cursor-not-allowed"
                    placeholder="Remboursement pour..."
                  />
                </div>

                {/* Avertissement pour remboursement direct */}
                <div className="bg-warning-10 border border-warning-20 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-warning mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-warning-dark">
                      <p className="font-semibold mb-1">Remboursement direct</p>
                      <p className="text-xs">Le montant sera remboursé directement sur la carte bancaire du client. La commission de la plateforme ne sera pas remboursée.</p>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setShowCreditModal(false);
                  setCreditOrder(null);
                  setCreditAmount('');
                  setCreditDescription('');
                }}
                disabled={processingRefund}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleRefundSubmit}
                disabled={(() => {
                  // Vérifications de base
                  if (!creditAmount || processingRefund) return true;

                  const amount = Number.parseFloat(creditAmount);
                  if (Number.isNaN(amount) || amount <= 0) return true;

                  // Vérifier si le montant dépasse le maximum remboursable
                  if (creditOrder?.products?.[creditProductIndex]) {
                    const product = creditOrder.products[creditProductIndex];
                    const productAmount = Number.parseFloat(product.amount || '0');
                    const shippingCost = includeShipping ? (creditOrder.deliveryInfo?.shippingCost || 0) : 0;
                    const totalAmount = productAmount + shippingCost;
                    const alreadyRefundedInCents = product.amountRefunded || 0;
                    const alreadyRefunded = typeof alreadyRefundedInCents === 'number'
                      ? alreadyRefundedInCents / 100
                      : Number.parseFloat(alreadyRefundedInCents.toString()) / 100;
                    const maxRefundable = includeShipping
                      ? totalAmount - alreadyRefunded
                      : productAmount - alreadyRefunded;

                    // Griser si le montant dépasse le maximum ou s'il n'y a rien à rembourser
                    if (amount > maxRefundable || maxRefundable <= 0) return true;
                  }

                  return false;
                })()}
                isLoading={processingRefund}
              >
                {processingRefund ? 'Remboursement en cours...' : 'Rembourser directement'}
              </Button>
            </DialogActions>
          </>
        )}
      </TailwindDialog>

      {/* Modal de résolution de problème de livraison (Parcel Resolver) */}
      <TailwindDialog
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        title="Résoudre le problème de livraison"
        maxWidth="lg"
        variant="themed"
      >
        <DialogContent className="p-0 overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
              {(() => {
                const isLockedAfterParcel =
                  Boolean((selectedOrder?.deliveryInfo as any)?.parcelId) ||
                  Boolean((selectedOrder?.deliveryInfo as any)?.trackingNumber) ||
                  Boolean((selectedOrder?.deliveryInfo as any)?.labelPrinterUrl);
                if (!isLockedAfterParcel) return null;
                return (
                  <div className="bg-warning-10 border border-warning-20 rounded-xl p-4 flex items-start gap-3">
                    <div className="p-2 bg-warning-20 rounded-lg flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-warning-dark" />
                    </div>
                    <p className="text-xs text-foreground leading-relaxed">
                      Un colis a déjà été généré pour cette commande. Le poids, le transporteur et la méthode de livraison sont verrouillés.
                    </p>
                  </div>
                );
              })()}
              {/* Note d'information */}
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-start gap-3">
                <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                  <Info className="w-5 h-5 text-primary" />
                </div>
                <p className="text-xs text-foreground leading-relaxed">
                  Corrigez l'adresse ou modifiez le mode de livraison pour débloquer la création du colis.
                  En cas d'adresse trop longue dans "Rue / Voie" (plus de 32 caractères), utilisez le "Complément d'adresse".
                </p>
              </div>

              {/* Section 1: Adresse */}
              <div className="space-y-4">
                <h4 className="font-bold text-sm text-foreground flex items-center gap-2">
                  <div className="w-6 h-6 bg-background-tertiary rounded flex items-center justify-center">
                    <Home className="w-3.5 h-3.5" />
                  </div>
                  Informations de destination
                </h4>

                <div className="grid grid-cols-12 gap-4">
                  {/* Recherche auto */}
                  <div className="col-span-12">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Recherche Google</label>
                    <GooglePlacesSearch
                      placeholder="Rechercher une adresse..."
                      value={addressSearchText}
                      onChange={setAddressSearchText}
                      onAddressSelect={(address, city, postalCode, country, houseNumber, street) => {
                        handleAddressSelect(address, city, postalCode, country, houseNumber, street);
                        setAddressSearchText('');
                      }}
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 transition-all font-medium text-sm"
                    />
                  </div>

                  <div className="col-span-4">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">N°</label>
                    <input
                      type="text"
                      value={addressForm.houseNumber}
                      onChange={(e) => setAddressForm({ ...addressForm, houseNumber: e.target.value })}
                      placeholder="Ex: 14"
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold text-center"
                    />
                  </div>

                  <div className="col-span-8">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Rue / Voie (Max 32 car.)</label>
                    <input
                      type="text"
                      value={addressForm.address}
                      maxLength={32}
                      onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                      placeholder="Ex: Boulevard de Richelieu"
                      className={`w-full px-4 py-2.5 bg-background-tertiary border rounded-xl focus:ring-2 focus:ring-primary/20 font-medium ${addressForm.address.length >= 32 ? 'border-warning bg-warning-10' : 'border-border'}`}
                    />
                    {addressForm.address.length >= 32 && (
                      <p className="text-[10px] text-warning-dark mt-1 font-medium italic">Limite atteinte, utilisez le complément ci-dessous</p>
                    )}
                  </div>

                  <div className="col-span-12">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Complément d'adresse (Address Line 2)</label>
                    <input
                      type="text"
                      value={addressForm.addressLine2 || ''}
                      onChange={(e) => setAddressForm({ ...addressForm, addressLine2: e.target.value })}
                      placeholder="Ex: Bâtiment B, Étage 3, Appt 42"
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-medium"
                    />
                  </div>

                  <div className="col-span-5">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Code Postal</label>
                    <input
                      type="text"
                      value={addressForm.postalCode}
                      onChange={(e) => setAddressForm({ ...addressForm, postalCode: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold"
                    />
                  </div>

                  <div className="col-span-7">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Ville</label>
                    <input
                      type="text"
                      value={addressForm.city}
                      onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold"
                    />
                  </div>

                  <div className="col-span-12">
                    <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">Pays</label>
                    <select
                      value={addressForm.country}
                      onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                      className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold"
                    >
                      <option value="FR">🇫🇷 France</option>
                      <option value="BE">🇧🇪 Belgique</option>
                      <option value="ES">🇪🇸 Espagne</option>
                      <option value="IT">🇮🇹 Italie</option>
                      <option value="DE">🇩🇪 Allemagne</option>
                      <option value="NL">🇳🇱 Pays-Bas</option>
                      <option value="PT">🇵🇹 Portugal</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 2: Mode de livraison */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h4 className="font-bold text-sm text-foreground flex items-center gap-2">
                  <div className="w-6 h-6 bg-background-tertiary rounded flex items-center justify-center">
                    <Truck className="w-3.5 h-3.5" />
                  </div>
                  Mode de livraison
                </h4>

                {(() => {
                  const isLockedAfterParcel =
                    Boolean((selectedOrder as any)?.parcelGenerated?.parcelId) ||
                    Boolean((selectedOrder as any)?.parcelGenerated?.trackingNumber) ||
                    Boolean((selectedOrder as any)?.parcelGenerated?.labelPrinterUrl);

                  const currentProvider = String(selectedOrder?.deliveryInfo?.provider || '').toLowerCase();
                  const currentMethod = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
                  const currentCarrier = (currentMethod as any)?.carrier ?? (selectedOrder?.deliveryInfo as any)?.carrier;
                  const baseIsHomeDelivery =
                    typeof selectedOrder?.deliveryInfo?.isHomeDelivery === 'boolean'
                      ? selectedOrder?.deliveryInfo?.isHomeDelivery
                      : currentMethod?.isHomeDelivery;

                  const methods = getShippingMethodsForCountry(addressForm.country)
                    .filter((m) => {
                      if (currentProvider === 'sendcloud' || currentProvider === 'sendcloud_api_key') {
                        if (String(m.provider || '').toLowerCase() !== currentProvider) return false;
                        if (currentCarrier && (m as any).carrier && (m as any).carrier !== currentCarrier) return false;
                        if (typeof baseIsHomeDelivery === 'boolean' && m.isHomeDelivery !== baseIsHomeDelivery) return false;
                      }
                      return true;
                    })
                    .filter((m) => {
                      const w = Number(parcelWeightKg) || 0;
                      const min = (m as any).minWeight;
                      const max = (m as any).maxWeight;
                      if (typeof min === 'number' && Number.isFinite(min) && w < min) return false;
                      if (typeof max === 'number' && Number.isFinite(max) && w > max) return false;
                      return true;
                    })
                    .filter((m) => {
                      const q = methodSearch.trim().toLowerCase();
                      if (!q) return true;
                      const carrier = String((m as any).carrier || '').toLowerCase();
                      return (
                        String(m.name || '').toLowerCase().includes(q) ||
                        String(m.description || '').toLowerCase().includes(q) ||
                        carrier.includes(q)
                      );
                    });

                  const showSendcloudHint = currentProvider === 'sendcloud' || currentProvider === 'sendcloud_api_key';
                  const selectedMethod = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
                  const selectedMethodOutOfRange = (() => {
                    if (!selectedMethod) return false;
                    const w = Number(parcelWeightKg) || 0;
                    const min = (selectedMethod as any).minWeight;
                    const max = (selectedMethod as any).maxWeight;
                    if (typeof min === 'number' && Number.isFinite(min) && w < min) return true;
                    if (typeof max === 'number' && Number.isFinite(max) && w > max) return true;
                    return false;
                  })();

                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-12 sm:col-span-4">
                          <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">
                            Poids (kg)
                          </label>
                          <input
                            type="number"
                            min={0.01}
                            step={0.01}
                            value={Number.isFinite(parcelWeightKg) ? parcelWeightKg : 0.5}
                            onChange={(e) => setParcelWeightKg(Number(e.target.value) || 0.5)}
                            disabled={isLockedAfterParcel}
                            className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                          <p className="text-[10px] text-foreground-secondary mt-1">
                            {showSendcloudHint
                              ? 'Sendcloud : filtre pays + poids + type (domicile/relais). Carrier verrouillé.'
                              : 'Mondial Relay : changer le poids suffit.'}
                          </p>
                        </div>
                        <div className="col-span-12 sm:col-span-8">
                          <label className="block text-[11px] font-bold text-foreground-secondary uppercase tracking-wider mb-1.5">
                            Rechercher une méthode
                          </label>
                          <input
                            type="text"
                            value={methodSearch}
                            onChange={(e) => setMethodSearch(e.target.value)}
                            disabled={isLockedAfterParcel}
                            placeholder={showSendcloudHint ? 'Nom / description / carrier' : 'Nom / description'}
                            className="w-full px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {selectedMethodOutOfRange && (
                        <div className="p-3 rounded-xl border border-warning-20 bg-warning-10 text-[12px] text-warning-dark">
                          Le poids dépasse la plage de la méthode sélectionnée. Vous devez choisir une autre méthode compatible.
                        </div>
                      )}

                      {methods.length === 0 ? (
                        <div className="p-3 rounded-xl border border-border bg-background-tertiary text-[12px] text-foreground-secondary">
                          Aucune méthode compatible avec ce pays, ce poids et ce type de livraison.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3">
                          {methods.map((method) => (
                    <div
                      key={method.id}
                      onClick={() => {
                        if (isLockedAfterParcel) return;
                        handleMethodChange(method.id);
                      }}
                      className={`flex items-center justify-between p-3.5 rounded-xl border-2 transition-all ${isLockedAfterParcel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${selectedMethodId === method.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/10'
                        : 'border-border bg-background-tertiary hover:border-border-medium'
                        }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-foreground mb-0.5">{method.name}</div>
                        <div className="text-[11px] text-foreground-secondary truncate">{method.description}</div>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <div className="font-black text-sm text-foreground">€{method.getPriceForCountry(addressForm.country).toFixed(2)}</div>
                        <div className="text-[10px] font-bold text-primary uppercase tracking-tight">
                          {method.isHomeDelivery ? '🏠 Domicile' : '📍 Point relais'}
                        </div>
                      </div>
                    </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Section 3: Point Relais */}
              {(() => {
                const method = selectedMethodId ? getShippingMethodById(selectedMethodId) : null;
                if (!method || method.isHomeDelivery) return null;

                return (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <h4 className="font-bold text-sm text-foreground flex items-center gap-2">
                      <div className="w-6 h-6 bg-background-tertiary rounded flex items-center justify-center">
                        <MaterialIcon icon="lan" className="text-[16px]" />
                      </div>
                      Sélection du Point Relais
                    </h4>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={pickupSearchPostalCode}
                        onChange={(e) => setPickupSearchPostalCode(e.target.value)}
                        placeholder="Code postal..."
                        className="flex-1 px-4 py-2.5 bg-background-tertiary border border-border rounded-xl focus:ring-2 focus:ring-primary/20 font-bold"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearchPickupPoints();
                          }
                        }}
                      />
                      <Button
                        variant="secondary"
                        onClick={(e: any) => {
                          e.preventDefault();
                          handleSearchPickupPoints();
                        }}
                        disabled={isSearchingPickup || !pickupSearchPostalCode}
                        className="rounded-xl px-6"
                        leftIcon={isSearchingPickup ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      >
                        Chercher
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                      {pickupPoints.map((point) => (
                        <div
                          key={point.id}
                          onClick={() => setSelectedPickupPoint(point)}
                          className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedPickupPoint?.id === point.id
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-border bg-background-tertiary hover:border-border-medium'
                            }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="font-bold text-xs text-foreground mb-1">{point.name}</div>
                              <div className="text-[11px] text-foreground-secondary leading-tight">
                                {point.address}, {point.postalCode} {point.city}
                              </div>
                            </div>
                            {selectedPickupPoint?.id === point.id && (
                              <Badge variant="stripe" size="sm">SÉLECTIONNÉ</Badge>
                            )}
                          </div>
                        </div>
                      ))}

                      {selectedPickupPoint && !pickupPoints.some(p => p.id === selectedPickupPoint.id) && (
                        <div className="p-3 rounded-xl border border-primary bg-primary/10">
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="font-bold text-xs text-foreground mb-1">{selectedPickupPoint.name}</div>
                              <div className="text-[11px] text-foreground-secondary leading-tight">
                                {selectedPickupPoint.address}, {selectedPickupPoint.postalCode} {selectedPickupPoint.city}
                              </div>
                            </div>
                            <Badge variant="stripe" size="sm">ACTUEL</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>

          {/* Footer Fixe */}
          <div className="flex-shrink-0 p-4 sm:p-6 bg-surface border-t border-border flex items-center justify-between gap-4">
              <button
                onClick={() => setShowAddressModal(false)}
                className="px-6 py-2.5 text-sm font-bold text-foreground-secondary hover:text-foreground transition-colors"
              >
                Annuler
              </button>
              {canManageOrdersForOrg && (
                <button
                  onClick={handleUpdateAndRegenerate}
                  disabled={(() => {
                    if (isRegeneratingParcel) return true;
                    const isLockedAfterParcel =
                      Boolean((selectedOrder as any)?.parcelGenerated?.parcelId) ||
                      Boolean((selectedOrder as any)?.parcelGenerated?.trackingNumber) ||
                      Boolean((selectedOrder as any)?.parcelGenerated?.labelPrinterUrl);
                    return isLockedAfterParcel;
                  })()}
                  className="flex-1 sm:flex-none px-10 py-3 bg-gradient-primary text-white font-black text-sm rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transform transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {isRegeneratingParcel ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {isManualParcelMode ? 'Enregistrer et Payer' : 'Enregistrer et Régénérer'}
                    </>
                  )}
                </button>
              )}
          </div>
        </DialogContent>
      </TailwindDialog>

      {/* Widget Circle Progress Flottant */}
      {
        stats.paid > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            {(() => {
              const totalPaidOrders = stats.paid;
              const readyPercentage = totalPaidOrders > 0 ? (stats.preparation.ready / totalPaidOrders) * 100 : 0;
              const inProgressPercentage = totalPaidOrders > 0 ? (stats.preparation.inProgress / totalPaidOrders) * 100 : 0;
              const toPreparePercentage = totalPaidOrders > 0 ? (stats.preparation.toPrepare / totalPaidOrders) * 100 : 0;

              return (
                <Tooltip
                  title={readyPercentage === 100
                    ? "🎉 Toutes les commandes sont prêtes ! Excellent travail !"
                    : `${stats.preparation.toPrepare} à faire • ${stats.preparation.inProgress} en cours • ${stats.preparation.ready} prêt`
                  }
                  placement="left"
                  arrow
                >
                  <div className="relative w-16 h-16 group cursor-pointer">
                    {/* Fond avec ombre et animation */}
                    <div
                      className={`absolute inset-0 rounded-full shadow-lg border transition-all duration-500 group-hover:shadow-xl group-hover:scale-105 ${readyPercentage === 100
                        ? 'bg-success-10 border-success-20 shadow-lg'
                        : 'bg-surface border-border'
                        }`}
                      style={readyPercentage === 100 ? {
                        animation: 'pulse 0.8s ease-in-out 4',
                        animationFillMode: 'forwards'
                      } : {}}
                    ></div>

                    {/* Circle Progress SVG avec segments multiples */}
                    <svg className="w-full h-full transform -rotate-90 relative z-10" viewBox="0 0 36 36">
                      {/* Cercle de fond */}
                      <path
                        className="text-foreground-secondary"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                      />

                      {/* Segment À faire (orange) */}
                      {toPreparePercentage > 0 && (
                        <path
                          className="text-orange-400"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${toPreparePercentage}, 100`}
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out'
                          }}
                        />
                      )}

                      {/* Segment En cours (bleu) */}
                      {inProgressPercentage > 0 && (
                        <path
                          className="text-blue-500"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${inProgressPercentage}, 100`}
                          strokeDashoffset={`${-toPreparePercentage}`}
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out, stroke-dashoffset 0.8s ease-in-out'
                          }}
                        />
                      )}

                      {/* Segment Prêt (vert) */}
                      {readyPercentage > 0 && (
                        <path
                          className="text-green-500"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${readyPercentage}, 100`}
                          strokeDashoffset={`${-(toPreparePercentage + inProgressPercentage)}`}
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out, stroke-dashoffset 0.8s ease-in-out'
                          }}
                        />
                      )}
                    </svg>

                    {/* Contenu centré avec animation */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                      {readyPercentage === 100 ? (
                        <>
                          {/* Animation Done - Icône de célébration */}
                          <div
                            className="animate-bounce"
                            style={{
                              animation: 'bounce 1s ease-in-out 3',
                              animationFillMode: 'forwards'
                            }}
                          >
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                          <span
                            className="text-xs font-bold text-success mt-0.5"
                            style={{
                              animation: 'pulse 0.5s ease-in-out 6',
                              animationFillMode: 'forwards'
                            }}
                          >
                            Done!
                          </span>

                          {/* Effet de confettis */}
                          <div className="absolute inset-0 pointer-events-none">
                            {Array.from({ length: 8 }, (_, i) => (
                              <div
                                key={`confetti-${i}`}
                                className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                                style={{
                                  top: '50%',
                                  left: '50%',
                                  transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-20px)`,
                                  animation: `ping 0.4s ease-out ${i * 0.1}s 1`,
                                  animationFillMode: 'forwards'
                                }}
                              />
                            ))}
                          </div>

                          {/* Effet de glow */}
                          <div
                            className="absolute inset-0 bg-green-200 rounded-full opacity-30"
                            style={{
                              animation: 'ping 1s ease-out 3',
                              animationFillMode: 'forwards'
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <PackageCheck className="h-4 w-4 text-primary group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-xs font-bold text-foreground mt-0.5 group-hover:text-primary transition-colors duration-300">
                            {readyPercentage.toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Tooltip>
              );
            })()}
          </div>
        )
      }

      {/* Widget Circle Progress Flottant */}
      {
        stats.paid > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            {(() => {
              const totalPaidOrders = stats.paid;
              const readyPercentage = totalPaidOrders > 0 ? (stats.preparation.ready / totalPaidOrders) * 100 : 0;
              const inProgressPercentage = totalPaidOrders > 0 ? (stats.preparation.inProgress / totalPaidOrders) * 100 : 0;
              const toPreparePercentage = totalPaidOrders > 0 ? (stats.preparation.toPrepare / totalPaidOrders) * 100 : 0;

              return (
                <Tooltip
                  title={readyPercentage === 100
                    ? "🎉 Toutes les commandes sont prêtes ! Excellent travail !"
                    : `${stats.preparation.toPrepare} à faire • ${stats.preparation.inProgress} en cours • ${stats.preparation.ready} prêt`
                  }
                  placement="left"
                  arrow
                >
                  <div className="relative w-16 h-16 group cursor-pointer">
                    {/* Fond avec ombre et animation */}
                    <div
                      className={`absolute inset-0 rounded-full shadow-lg border transition-all duration-500 group-hover:shadow-xl group-hover:scale-105 ${readyPercentage === 100
                        ? 'bg-success-10 border-success-20 shadow-lg'
                        : 'bg-surface border-border'
                        }`}
                      style={readyPercentage === 100 ? {
                        animation: 'pulse 0.8s ease-in-out 4',
                        animationFillMode: 'forwards'
                      } : {}}
                    ></div>

                    {/* Circle Progress SVG avec segments multiples */}
                    <svg className="w-full h-full transform -rotate-90 relative z-10" viewBox="0 0 36 36">
                      {/* Cercle de fond */}
                      <path
                        className="text-foreground-secondary"
                        stroke="currentColor"
                        strokeWidth="3"
                        fill="none"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                      />

                      {/* Segment À faire (orange) */}
                      {toPreparePercentage > 0 && (
                        <path
                          className="text-orange-400"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${toPreparePercentage}, 100`}
                          d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out'
                          }}
                        />
                      )}

                      {/* Segment En cours (bleu) */}
                      {inProgressPercentage > 0 && (
                        <path
                          className="text-blue-500"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${inProgressPercentage}, 100`}
                          strokeDashoffset={`${-toPreparePercentage}`}
                          d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out, stroke-dashoffset 0.8s ease-in-out'
                          }}
                        />
                      )}

                      {/* Segment Prêt (vert) */}
                      {readyPercentage > 0 && (
                        <path
                          className="text-green-500"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={`${readyPercentage}, 100`}
                          strokeDashoffset={`${-(toPreparePercentage + inProgressPercentage)}`}
                          d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                          style={{
                            transition: 'stroke-dasharray 0.8s ease-in-out, stroke-dashoffset 0.8s ease-in-out'
                          }}
                        />
                      )}
                    </svg>

                    {/* Contenu centré avec animation */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                      {readyPercentage === 100 ? (
                        <>
                          {/* Animation Done - Icône de célébration */}
                          <div
                            className="animate-bounce"
                            style={{
                              animation: 'bounce 1s ease-in-out 3',
                              animationFillMode: 'forwards'
                            }}
                          >
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          </div>
                          <span
                            className="text-xs font-bold text-success mt-0.5"
                            style={{
                              animation: 'pulse 0.5s ease-in-out 6',
                              animationFillMode: 'forwards'
                            }}
                          >
                            Done!
                          </span>

                          {/* Effet de confettis */}
                          <div className="absolute inset-0 pointer-events-none">
                            {[...Array(8)].map((_, i) => (
                              <div
                                key={i}
                                className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                                style={{
                                  top: '50%',
                                  left: '50%',
                                  transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-20px)`,
                                  animation: `ping 0.4s ease-out ${i * 0.1}s 1`,
                                  animationFillMode: 'forwards'
                                }}
                              />
                            ))}
                          </div>

                          {/* Effet de glow */}
                          <div
                            className="absolute inset-0 bg-green-200 rounded-full opacity-30"
                            style={{
                              animation: 'ping 1s ease-out 3',
                              animationFillMode: 'forwards'
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <PackageCheck className="h-4 w-4 text-primary group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-xs font-bold text-foreground mt-0.5 group-hover:text-primary transition-colors duration-300">
                            {readyPercentage.toFixed(0)}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Tooltip>
              );
            })()}
          </div>
        )
      }

      {/* (supprimé) Modal de création de retour Sendcloud */}

      {/* Floating Action Bar for Batch Parcel Generation - Compact & Visible */}
      {
        isBatchSelectionMode && selectedOrderIds.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-surface border-2 border-primary shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl px-5 py-3.5 flex items-center gap-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/20 shadow-lg border border-primary/30">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-foreground text-base">
                    {selectedOrderIds.size} commande{selectedOrderIds.size > 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-foreground-secondary">
                    Prêtes pour génération
                  </p>
                </div>
              </div>
              <div className="h-10 w-px bg-border" />
              <div className="flex items-center gap-3">
                <button
                  onClick={exitBatchSelectionMode}
                  className="flex items-center gap-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-background-secondary border border-border hover:border-primary/50 transition-all px-4 py-2 rounded-lg"
                >
                  <X className="h-4 w-4" />
                  Annuler
                </button>
                <button
                  onClick={() => setShowBatchParcelModal(true)}
                  className="flex items-center gap-2 shadow-xl text-sm bg-primary text-white hover:bg-primary-dark font-bold transition-all px-5 py-2.5 rounded-lg hover:scale-105"
                >
                  <Truck className="h-4 w-4" />
                  Générer les colis
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Batch Parcel Modal */}
      {
        idorga && (
          <BatchParcelModal
            open={showBatchParcelModal}
            onClose={() => {
              // Ne pas appeler exitBatchSelectionMode() ici pour garder la sélection
              setShowBatchParcelModal(false);
            }}
            selectedOrders={selectedOrders}
            organizationId={idorga}
            stripePublishableKey={configuration?.stripeApiKeyConfig?.publishableKey}
            onSuccess={() => {
              setShowBatchParcelModal(false);
              exitBatchSelectionMode(); // Désélectionner uniquement après succès
              refreshOrders();
            }}
          />
        )
      }
    </div >
  );
};

export default NewAdminDashboard;
